import React, { useState, useEffect, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import SuperAdmin from './SuperAdmin';
import ClientAdmin from './ClientAdmin';
import './App.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';



// --- Policy history helpers ---
const todayISO = () => new Date().toISOString().slice(0, 10);
const dayBefore = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  dt.setDate(dt.getDate() - 1);
  return dt.toISOString().slice(0, 10);
};

// Mirrors the backend classify_query so the user sees how their input is read
// before searching (an email is never treated as a policy number, etc.).
const classifySearch = (q) => {
  const s = (q || '').trim();
  if (!s) return null;
  if (s.includes('@')) return 'email';
  const digits = s.replace(/\D/g, '');
  if (digits.length === 13 && /^\d+$/.test(s.replace(/\s/g, ''))) return 'ID number';
  let norm = digits;
  if (norm.startsWith('27') && norm.length === 11) norm = '0' + norm.slice(2);
  if (norm.length === 10 && norm.startsWith('0')) return 'cell number';
  return 'policy number';
};
const toISODate = (d) => {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 10);
};

const TYPE_ICONS = {
  vehicle: '🚗',
  motorcycle: '🏍️',
  householder: '🏠',
  building: '🏢',
  allrisk: '💎',
  liability: '⚖️',
  watercraft: '🚤',
  caravan: '🚐',
  personal_accident: '🏥',
  legal: '📜',
  electronic: '💻',
  special_risk: '⭐',
};

// Claim status colors
const CLAIM_STATUS_COLORS = {
  '1': '#3b82f6',  // In Progress - blue
  '2': '#f59e0b',  // In Progress - previously finalised - amber
  '3': '#10b981',  // Finalised - green
  '4': '#8b5cf6',  // Finalised - awaiting recovery - purple
  '5': '#6b7280',  // Not taken up - gray
  '6': '#ef4444',  // Repudiated - red
  '7': '#8b5cf6',  // Finalised - awaiting third party - purple
  '8': '#ef4444',  // Repudiated (payment to supplier) - red
  '9': '#8b5cf6',  // Finalised - awaiting salvage - purple
  '10': '#8b5cf6', // Finalised - awaiting third party claim - purple
};

// Claims Icon SVG
const ClaimsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="currentColor" strokeWidth="2"/>
    <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M9 16H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Attachments/Paperclip Icon SVG
const AttachmentsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.44 11.05L12.25 20.24C11.1242 21.3658 9.59723 21.9983 8.00502 21.9983C6.41282 21.9983 4.88584 21.3658 3.76002 20.24C2.6342 19.1142 2.00171 17.5872 2.00171 15.995C2.00171 14.4028 2.6342 12.8758 3.76002 11.75L12.95 2.56C13.7006 1.80944 14.7186 1.38778 15.78 1.38778C16.8415 1.38778 17.8594 1.80944 18.61 2.56C19.3606 3.31057 19.7822 4.32855 19.7822 5.39C19.7822 6.45146 19.3606 7.46944 18.61 8.22L9.41002 17.41C9.03473 17.7853 8.52574 17.9961 7.99502 17.9961C7.4643 17.9961 6.9553 17.7853 6.58002 17.41C6.20473 17.0347 5.9939 16.5257 5.9939 15.995C5.9939 15.4643 6.20473 14.9553 6.58002 14.58L15.07 6.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Hub Network Logo SVG - Central hub connecting to source nodes
const HubNetworkLogo = () => (
  <svg width="36" height="36" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hubGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
    </defs>
    {/* Connection lines from center to outer nodes */}
    <line x1="20" y1="20" x2="20" y2="5" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
    <line x1="20" y1="20" x2="34" y2="12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
    <line x1="20" y1="20" x2="34" y2="28" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
    <line x1="20" y1="20" x2="6" y2="28" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
    <line x1="20" y1="20" x2="6" y2="12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
    {/* Outer nodes (sources) */}
    <circle cx="20" cy="5" r="4" fill="#60a5fa"/>
    <circle cx="34" cy="12" r="4" fill="#60a5fa"/>
    <circle cx="34" cy="28" r="4" fill="#60a5fa"/>
    <circle cx="6" cy="28" r="4" fill="#60a5fa"/>
    <circle cx="6" cy="12" r="4" fill="#60a5fa"/>
    {/* Center hub (PolicyLink) */}
    <circle cx="20" cy="20" r="7" fill="url(#hubGradient)"/>
    <circle cx="20" cy="20" r="3" fill="#ffffff"/>
  </svg>
);

// Refresh Icon SVG
const RefreshIcon = ({ spinning }) => (
  <svg 
    width="14" 
    height="14" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={spinning ? 'spin' : ''}
  >
    <path 
      d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    <path 
      d="M21 3V8H16" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Search Spinner Icon
const SearchSpinner = () => (
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className="search-spinner"
  >
    <circle 
      cx="12" 
      cy="12" 
      r="10" 
      stroke="currentColor" 
      strokeWidth="3" 
      strokeLinecap="round"
      strokeDasharray="31.4 31.4"
      strokeDashoffset="0"
    />
  </svg>
);

// Clock Icon for Recent
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// Chevron Icon
const ChevronIcon = ({ direction = 'right' }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: direction === 'left' ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
  >
    <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Download Icon
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// PDF Icon
const PdfIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 13H10.5C11.3284 13 12 13.6716 12 14.5C12 15.3284 11.3284 16 10.5 16H9V13ZM9 13V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 13H16.5C17.3284 13 18 13.6716 18 14.5V16.5C18 17.3284 17.3284 18 16.5 18H15V13Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Excel Icon
const ExcelIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 13L12 18M12 13L9 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 13V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 15.5H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ============================================
// AUTH HELPER FUNCTIONS
// ============================================

const getToken = () => localStorage.getItem('policylink_token');
const getUser = () => {
  const user = localStorage.getItem('policylink_user');
  return user ? JSON.parse(user) : null;
};

const setAuth = (token, user) => {
  localStorage.setItem('policylink_token', token);
  localStorage.setItem('policylink_user', JSON.stringify(user));
};

const clearAuth = () => {
  localStorage.removeItem('policylink_token');
  localStorage.removeItem('policylink_user');
};

// Fetch with auth token
const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  
  const response = await fetch(url, { ...options, headers });
  
  // If unauthorized, clear auth and reload
  if (response.status === 401) {
    clearAuth();
    window.location.reload();
    throw new Error('Session expired');
  }
  
  return response;
};

// ============================================
// PASSWORD VALIDATION HELPERS
// ============================================

function getPasswordRules(password) {
  return [
    { label: 'At least 8 characters', passed: password.length >= 8 },
    { label: 'At most 128 characters', passed: password.length <= 128 },
    { label: 'One uppercase letter (A-Z)', passed: /[A-Z]/.test(password) },
    { label: 'One lowercase letter (a-z)', passed: /[a-z]/.test(password) },
    { label: 'One digit (0-9)', passed: /\d/.test(password) },
    { label: 'One special character (!@#$...)', passed: /[!@#$%^&*()\-_+=[\]{};:'",.<>?/\\|`~]/.test(password) },
    { label: 'No spaces', passed: password.length === 0 || !/ /.test(password) },
  ];
}

function PasswordRulesChecklist({ password }) {
  const rules = getPasswordRules(password);
  if (!password) return null;
  return (
    <div className="password-rules">
      {rules.map((rule, i) => (
        <div key={i} className={`password-rule ${rule.passed ? 'pass' : 'fail'}`}>
          <span className="password-rule-icon">{rule.passed ? '\u2713' : '\u2717'}</span>
          {rule.label}
        </div>
      ))}
    </div>
  );
}

// Eye icons for the reveal toggle
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/**
 * PasswordInput — drop-in replacement for <input type="password">
 * with an eye icon to reveal/hide the value.
 * Accepts all standard <input> props.
 */
function PasswordInput({ value, onChange, placeholder, required, autoFocus, ...rest }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="password-input-wrapper">
      <input
        type={reveal ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        {...rest}
      />
      <button
        type="button"
        className="password-reveal-toggle"
        onClick={() => setReveal(r => !r)}
        aria-label={reveal ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        {reveal ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

// ============================================
// TIME FORMATTING HELPERS
// ============================================

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const groupViewsByTime = (views) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  
  const groups = { today: [], yesterday: [], thisWeek: [], older: [] };
  
  views.forEach(view => {
    const viewDate = new Date(view.viewed_at);
    if (viewDate >= today) groups.today.push(view);
    else if (viewDate >= yesterday) groups.yesterday.push(view);
    else if (viewDate >= lastWeek) groups.thisWeek.push(view);
    else groups.older.push(view);
  });
  
  return groups;
};

// ============================================
// AUTH SHELL — shared split-screen layout for all auth screens
// ============================================

function AuthShell({ heading, subtitle, children }) {
  return (
    <div className="auth-split">
      <div className="auth-brand">
        <div className="auth-brand-bg" aria-hidden="true"></div>
        <div className="auth-brand-inner">
          <div className="auth-logo-hero">
            <HubNetworkLogo />
          </div>
          <h1 className="auth-brand-title">PolicyLink</h1>
          <p className="auth-brand-tagline">Every policy, every source — one search.</p>
        </div>
        <div className="auth-brand-foot">
          <span className="auth-brand-mark">Velora</span>
          <span className="auth-brand-year">© {new Date().getFullYear()}</span>
        </div>
      </div>

      <div className="auth-form-side">
        <div className="auth-form-card">
          <div className="auth-form-header">
            <h2>{heading}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================
// LOGIN COMPONENT
// ============================================

function LoginScreen({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim(), password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Login failed');
      }

      const data = await response.json();
      setAuth(data.access_token, data.user);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <AuthShell heading="Sign in" subtitle="Welcome back. Enter your credentials below.">
      <form onSubmit={handleSubmit} className="login-form">
        {error && <div className="login-error">{error}</div>}

        <div className="login-field">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
          />
        </div>

        <div className="login-field">
          <label>Password</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>

        <button type="submit" className="login-button" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign in'}
        </button>

        <div className="login-footer-link">
          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/forgot-password')}
          >
            Forgot password?
          </button>
        </div>
      </form>
    </AuthShell>
  );
}

// ============================================
// FORCE CHANGE PASSWORD SCREEN
// ============================================

function ForceChangePasswordScreen({ user, onPasswordChanged }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rules = getPasswordRules(newPassword);
  const allPassed = rules.every(r => r.passed) && newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allPassed) return;
    setLoading(true);
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const data = await response.json();

      if (!response.ok) {
        const detail = data.detail;
        throw new Error(Array.isArray(detail) ? detail.join(', ') : detail || 'Failed to change password');
      }

      // Backend issues a fresh JWT (the old one's now invalid because
      // password_changed_at was bumped). Store it so this session continues.
      const updatedUser = { ...user, must_change_password: false };
      setAuth(data.access_token, updatedUser);
      onPasswordChanged(updatedUser);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <AuthShell heading="Change password" subtitle="You must set a new password before continuing.">
      <form onSubmit={handleSubmit} className="login-form">
        {error && <div className="login-error">{error}</div>}

        <div className="login-field">
          <label>New Password</label>
          <PasswordInput
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            required
            autoFocus
          />
        </div>

        <PasswordRulesChecklist password={newPassword} />

        <div className="login-field">
          <label>Confirm Password</label>
          <PasswordInput
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter new password"
            required
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <div className="password-rule fail" style={{ marginTop: '0.25rem' }}>
              <span className="password-rule-icon">{'\u2717'}</span>
              Passwords do not match
            </div>
          )}
        </div>

        <button type="submit" className="login-button" disabled={loading || !allPassed}>
          {loading ? 'Saving...' : 'Set Password'}
        </button>
      </form>
    </AuthShell>
  );
}

// ============================================
// CHANGE PASSWORD MODAL (self-service)
// ============================================

function ChangePasswordModal({ onClose, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const rules = getPasswordRules(newPassword);
  const allPassed = rules.every(r => r.passed)
    && newPassword === confirmPassword
    && confirmPassword.length > 0
    && currentPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allPassed) return;
    setLoading(true);
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        const detail = data.detail;
        throw new Error(Array.isArray(detail) ? detail.join(', ') : detail || 'Failed to change password');
      }

      // Backend issues a fresh JWT — swap it in so the next request doesn't
      // 401 (the old token is invalid because password_changed_at moved).
      setAuth(data.access_token, getUser());
      setSuccess(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1500);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>Change Password</h3>
          <button className="admin-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="password-success">Password changed successfully!</div>}

            <div className="admin-field">
              <label>Current Password</label>
              <PasswordInput value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required autoFocus />
            </div>
            <div className="admin-field">
              <label>New Password</label>
              <PasswordInput value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required />
            </div>
            <PasswordRulesChecklist password={newPassword} />
            <div className="admin-field">
              <label>Confirm New Password</label>
              <PasswordInput value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required />
              {confirmPassword && newPassword !== confirmPassword && (
                <div className="password-rule fail" style={{ marginTop: '0.25rem' }}>
                  <span className="password-rule-icon">{'\u2717'}</span>
                  Passwords do not match
                </div>
              )}
            </div>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={loading || !allPassed}>
              {loading ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// FORGOT PASSWORD SCREEN
// ============================================

function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });
      // Always show success — the backend always returns 200 for privacy.
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <AuthShell
      heading="Reset password"
      subtitle={submitted
        ? "Check your inbox for the reset link."
        : "Enter your email and we'll send you a link to set a new password."}
    >
      {submitted ? (
        <div className="login-form">
          <div className="password-success" style={{ marginBottom: '1rem' }}>
            If an account exists for <strong>{email}</strong>, a reset link has been sent. The link expires in 30 minutes.
          </div>
          <button
            type="button"
            className="login-button"
            onClick={() => navigate('/')}
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}

          <div className="login-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>

          <button type="submit" className="login-button" disabled={loading || !email}>
            {loading ? 'Sending...' : 'Send reset link'}
          </button>

          <div className="login-footer-link">
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/')}
            >
              Back to sign in
            </button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}

// ============================================
// RESET PASSWORD SCREEN (reached via email link)
// ============================================

function ResetPasswordScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const rules = getPasswordRules(newPassword);
  const allPassed = rules.every(r => r.passed)
    && newPassword === confirmPassword
    && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allPassed) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(Array.isArray(detail) ? detail.join(', ') : detail || 'Reset failed');
      }
      // Backend returns a fresh JWT + user info — store both so the user
      // lands in the app instead of having to log in again.
      if (data.access_token && data.user) {
        setAuth(data.access_token, data.user);
      }
      setSuccess(true);
      // Hard reload so App's top-level auth state re-initialises from the
      // freshly-written localStorage. A soft `navigate('/')` would keep
      // the stale in-memory state and bounce back to login.
      setTimeout(() => { window.location.href = '/'; }, 1000);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Missing token → friendly error state.
  if (!token) {
    return (
      <AuthShell heading="Invalid link" subtitle="This reset link is missing its token. Please request a new one.">
        <div className="login-form">
          <button
            type="button"
            className="login-button"
            onClick={() => navigate('/forgot-password')}
          >
            Request new link
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell heading="Set new password" subtitle="Choose a new password for your PolicyLink account.">
      <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="login-error">{error}</div>}
          {success && (
            <div className="password-success">
              Password set. Signing you in…
            </div>
          )}

          <div className="login-field">
            <label>New Password</label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              autoFocus
              disabled={success}
            />
          </div>

          <PasswordRulesChecklist password={newPassword} />

          <div className="login-field">
            <label>Confirm Password</label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              required
              disabled={success}
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <div className="password-rule fail" style={{ marginTop: '0.25rem' }}>
                <span className="password-rule-icon">{'\u2717'}</span>
                Passwords do not match
              </div>
            )}
          </div>

          <button type="submit" className="login-button" disabled={loading || !allPassed || success}>
            {loading ? 'Saving...' : success ? 'Saved' : 'Set new password'}
          </button>
        </form>
    </AuthShell>
  );
}

// ============================================
// CONNECTION LOADER (shown after login / session restore)
// ============================================

function ConnectionLoader({ onComplete, checkHealth, sources, healthLoading }) {
  const [phaseComplete, setPhaseComplete] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  // Trigger health check on mount
  useEffect(() => {
    checkHealth();
    // eslint-disable-next-line
  }, []);

  // When health check finishes with results
  useEffect(() => {
    if (!healthLoading && sources.length > 0 && !phaseComplete) {
      setPhaseComplete(true);
    }
  }, [healthLoading, sources, phaseComplete]);

  // When health check finishes with NO results (error / empty)
  useEffect(() => {
    if (!healthLoading && sources.length === 0 && !phaseComplete) {
      const timer = setTimeout(() => setPhaseComplete(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [healthLoading, sources, phaseComplete]);

  // After results shown, wait then fade out and transition
  useEffect(() => {
    if (phaseComplete) {
      const timer = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => onComplete(), 400);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [phaseComplete, onComplete]);

  return (
    <div className={`connection-loader ${fadeOut ? 'fade-out' : ''}`}>
      <div className="connection-loader-content">
        <div className="connection-loader-logo">
          <svg width="96" height="96" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="hubGradientLoader" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#60a5fa" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
            </defs>
            <line x1="20" y1="20" x2="20" y2="5" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
            <line x1="20" y1="20" x2="34" y2="12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
            <line x1="20" y1="20" x2="34" y2="28" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
            <line x1="20" y1="20" x2="6" y2="28" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
            <line x1="20" y1="20" x2="6" y2="12" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="20" cy="5" r="4" fill="#60a5fa"/>
            <circle cx="34" cy="12" r="4" fill="#60a5fa"/>
            <circle cx="34" cy="28" r="4" fill="#60a5fa"/>
            <circle cx="6" cy="28" r="4" fill="#60a5fa"/>
            <circle cx="6" cy="12" r="4" fill="#60a5fa"/>
            <circle cx="20" cy="20" r="7" fill="url(#hubGradientLoader)"/>
            <circle cx="20" cy="20" r="3" fill="#ffffff"/>
          </svg>
        </div>

        <h2 className="connection-loader-title">PolicyLink</h2>
        <p className="connection-loader-subtitle">
          {healthLoading ? 'Connecting to sources...' :
           sources.length > 0 ? 'All connections checked' :
           'Unable to reach sources'}
        </p>

        <div className="connection-loader-sources">
          {healthLoading && sources.length === 0 ? (
            <div className="connection-loader-spinner">
              <div className="spinner"></div>
            </div>
          ) : (
            sources.map((source, index) => (
              <div
                key={source.id}
                className="connection-loader-source"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span
                  className={`connection-loader-source-icon ${source.status}`}
                >
                  {source.status === 'online' ? '\u2713' :
                   source.status === 'degraded' ? '!' : '\u2717'}
                </span>
                <span className="connection-loader-source-name">{source.name}</span>
                <span className={`connection-loader-source-status ${source.status}`}>
                  {source.status === 'online' ? 'Connected' :
                   source.status === 'degraded' ? 'Degraded' :
                   source.status === 'disabled' ? 'Disabled' : 'Offline'}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ADMIN NAV LINK (used in header)
// ============================================

function AdminNavLink({ to, label }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <button
      className={`admin-nav-link ${isActive ? 'active' : ''}`}
      onClick={() => navigate(to)}
    >
      {label}
    </button>
  );
}

// ============================================
// SOURCE PICKER — Multi-select dropdown for choosing search sources
// ============================================
function SourcePicker({ sources, value, onChange, disabled, size = 'normal' }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  const allSelected = value === 'all';
  const selectedSet = useMemo(() => {
    if (allSelected) return new Set(sources.map(s => s.id));
    if (!value) return new Set();
    return new Set(value.split(','));
  }, [value, allSelected, sources]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setFilter('');
      }
    };
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open]);

  const onlineCount = sources.filter(s => s.status === 'online').length;

  const label = useMemo(() => {
    if (allSelected) return `All sources (${onlineCount} online)`;
    if (selectedSet.size === 0) return 'No sources';
    if (selectedSet.size === 1) {
      const s = sources.find(src => selectedSet.has(src.id));
      return s?.name || '1 source';
    }
    return `${selectedSet.size} sources`;
  }, [allSelected, selectedSet, sources, onlineCount]);

  const emit = (newSet) => {
    if (newSet.size === 0) return; // never zero — ignore
    if (newSet.size === sources.length) {
      onChange('all');
    } else {
      onChange(Array.from(newSet).join(','));
    }
  };

  const toggleSource = (id) => {
    if (allSelected) {
      const newSet = new Set(sources.map(s => s.id));
      newSet.delete(id);
      emit(newSet);
    } else {
      const newSet = new Set(selectedSet);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      emit(newSet);
    }
  };

  const selectAll = () => onChange('all');
  const selectOnline = () => {
    const onlineIds = sources.filter(s => s.status === 'online').map(s => s.id);
    if (onlineIds.length === 0) return;
    emit(new Set(onlineIds));
  };

  const filteredSources = sources.filter(s =>
    !filter || s.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className={`source-picker ${size}`} ref={ref}>
      <button
        type="button"
        className={`source-picker-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen(!open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="source-picker-label">{label}</span>
        <span className="source-picker-chevron" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="source-picker-popover" role="listbox">
          <div className="source-picker-presets">
            <button
              type="button"
              className={`source-picker-preset ${allSelected ? 'active' : ''}`}
              onClick={selectAll}
            >
              All
            </button>
            <button
              type="button"
              className="source-picker-preset"
              onClick={selectOnline}
              disabled={onlineCount === 0}
            >
              Online only
            </button>
          </div>

          {sources.length > 8 && (
            <div className="source-picker-search">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter sources..."
                autoFocus
              />
            </div>
          )}

          <div className="source-picker-list">
            {filteredSources.length === 0 ? (
              <div className="source-picker-empty">No matches</div>
            ) : (
              filteredSources.map(s => {
                const isChecked = selectedSet.has(s.id);
                const isOffline = s.status === 'offline';
                return (
                  <label
                    key={s.id}
                    className={`source-picker-item ${isChecked ? 'checked' : ''} ${isOffline ? 'offline' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSource(s.id)}
                    />
                    <span className={`source-picker-dot ${s.status}`} aria-hidden="true"></span>
                    <span className="source-picker-name">{s.name}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================
// MAIN APP COMPONENT
// ============================================

function App() {
  // Auth state
  const [user, setUser] = useState(getUser());
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
  const [mustChangePassword, setMustChangePassword] = useState(() => {
    const u = getUser();
    return u?.must_change_password || false;
  });
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyDetail, setPolicyDetail] = useState(null);
  // Policy history: when a past date is selected, the historical snapshot is
  // swapped into policyDetail (same shape) and the live detail is stashed here.
  const liveDetailRef = useRef(null);
  const [historyBanner, setHistoryBanner] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyDate, setHistoryDate] = useState('');
  // Version browser modal
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsMaxSeq, setVersionsMaxSeq] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const versionsEsRef = useRef(null);
  // The live (current) policy detail regardless of history view — used to source
  // quick-link dates for the history control.
  const liveDetail = liveDetailRef.current || policyDetail;
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(true);
  const [connectionChecked, setConnectionChecked] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  // Collapsed state for the fixed detail sub-sections (Client, Policy), persisted across sessions
  const [collapsedDetailSections, setCollapsedDetailSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pl_collapsed_sections') || '{}') || {};
    } catch {
      return {};
    }
  });
  const toggleDetailSection = (key) => {
    setCollapsedDetailSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('pl_collapsed_sections', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Slim the results column once a policy is open, so the detail panel gets the width
  const [resultsCollapsed, setResultsCollapsed] = useState(false);
  
  // Recent views state
  const [recentViews, setRecentViews] = useState([]);
  const [recentCollapsed, setRecentCollapsed] = useState(false);
  const [recentLoading, setRecentLoading] = useState(false);
  
  const [showConnections, setShowConnections] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'excel' | 'excel-rows' | null

  // Claims state
  const [claimsData, setClaimsData] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [claimsExpanded, setClaimsExpanded] = useState(false);
  const [claimTimeOpen, setClaimTimeOpen] = useState(false); // "Policy at claim time" cluster in the history bar
  const [itemModal, setItemModal] = useState(null); // { item, itemType }
  const [expandedClaims, setExpandedClaims] = useState({});
  const [claimDetails, setClaimDetails] = useState({});
  const [claimDetailLoading, setClaimDetailLoading] = useState({});

  // Attachments state
  const [attachmentsData, setAttachmentsData] = useState(null);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);

  // Extension metadata state
  const [extensionNames, setExtensionNames] = useState({});
  const [metadataLoading, setMetadataLoading] = useState(false);

  // Policy schedule document state
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);

  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);


  // Handle login
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    if (userData.must_change_password) {
      setMustChangePassword(true);
    } else {
      navigate('/', { replace: true });
    }
  };

  // Handle logout
  const handleLogout = () => {
    clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    setConnectionChecked(false);
    setSources([]);
    setSearchResults(null);
    setSelectedPolicy(null);
    setPolicyDetail(null);
    setRecentViews([]);
  };

  // Fetch recent views
  const fetchRecentViews = async () => {
    try {
      setRecentLoading(true);
      const response = await authFetch(`${API_BASE}/api/history/recent?limit=20`);
      const data = await response.json();
      setRecentViews(data.views || []);
    } catch (err) {
      console.error('Failed to fetch recent views:', err);
    }
    setRecentLoading(false);
  };

  // Log a policy view
  const logPolicyView = async (policy, detail) => {
    try {
      await authFetch(`${API_BASE}/api/history/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: policy.policy_number,
          source_id: policy.source_id,
          source_name: detail?.source_name || policy.source_name || 'Unknown',
          client_name: detail?.client?.name || null,
          status: detail?.status || policy.status || null,
        }),
      });
    } catch (err) {
      console.error('Failed to log policy view:', err);
    }
  };

  // Fetch claims for a policy
  const fetchClaims = async (policyNumber, sourceId) => {
    setClaimsLoading(true);
    setClaimsData(null);
    setClaimDetails({});
    setClaimDetailLoading({});
    try {
      const response = await authFetch(
        `${API_BASE}/api/claims/policy/${encodeURIComponent(policyNumber)}?source=${sourceId}`
      );
      const data = await response.json();
      setClaimsData(data);
    } catch (err) {
      console.error('Failed to fetch claims:', err);
      setClaimsData({ success: false, error: 'Failed to load claims' });
    }
    setClaimsLoading(false);
  };

  // Toggle individual claim expansion and fetch detail
  const toggleClaimExpanded = async (claimNumber) => {
    const isExpanding = !expandedClaims[claimNumber];
    setExpandedClaims(prev => ({
      ...prev,
      [claimNumber]: isExpanding
    }));

    // Claim documents come from the policy-level attachments fetch — kick it
    // off in the background if it hasn't run yet so the claim's docs can show.
    if (isExpanding && !attachmentsData && !attachmentsLoading &&
        selectedPolicy && (liveDetail?.internal_policy_number)) {
      fetchAttachments(selectedPolicy.policy_number, selectedPolicy.source_id, liveDetail.internal_policy_number);
    }

    // Fetch claim detail when expanding (if not already cached)
    if (isExpanding && !claimDetails[claimNumber] && selectedPolicy) {
      setClaimDetailLoading(prev => ({ ...prev, [claimNumber]: true }));
      try {
        const response = await authFetch(
          `${API_BASE}/api/claims/detail?claim=${encodeURIComponent(claimNumber)}&source=${selectedPolicy.source_id}`
        );
        const data = await response.json();
        if (data.success && data.claim) {
          setClaimDetails(prev => ({ ...prev, [claimNumber]: data.claim }));
        }
      } catch (err) {
        console.error('Failed to fetch claim detail:', err);
      }
      setClaimDetailLoading(prev => ({ ...prev, [claimNumber]: false }));
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRecentViews();
      // Only start polling after the ConnectionLoader has completed the initial check
      if (connectionChecked) {
        const interval = setInterval(() => {
          checkHealth(true);
        }, 60000);
        return () => clearInterval(interval);
      }
    }
  }, [isAuthenticated, connectionChecked]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowConnections(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
};
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkHealth = async (silent = false) => {
    if (!silent) setHealthLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/health`);
      const data = await response.json();
      setSources(data.sources);
      setLastChecked(new Date());
    } catch (err) {
      if (!silent) setError('Failed to connect to backend.');
    }
    if (!silent) setHealthLoading(false);
  };

  // Get sources to search (for immediate UI feedback)
  const getSourcesToSearch = () => {
    if (selectedSource === 'all') {
      return sources.filter(s => s.status === 'online').map(s => ({ id: s.id, name: s.name }));
    }
    if (selectedSource.includes(',')) {
      const ids = new Set(selectedSource.split(','));
      return sources
        .filter(s => ids.has(s.id) && s.status === 'online')
        .map(s => ({ id: s.id, name: s.name }));
    }
    const source = sources.find(s => s.id === selectedSource);
    return source ? [{ id: source.id, name: source.name }] : [];
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const sourcesToSearch = getSourcesToSearch();
    
    // IMMEDIATE feedback - show searching state right away
    setLoading(true);
    setError(null);
    setSearchResults({
      query: searchQuery,
      results: [],
      total_policies_found: 0,
      searching: true,
      initializing: true,
      remaining: sourcesToSearch.length,
      pending_sources: sourcesToSearch,
    });
    setSelectedPolicy(null);
    setPolicyDetail(null);
    setResultsCollapsed(false);
    setExpandedSections({});

    try {
      const token = getToken();
      const eventSource = new EventSource(
        `${API_BASE}/api/search/stream?q=${encodeURIComponent(searchQuery)}&source=${selectedSource}&token=${token}`
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'start') {
          setSearchResults(prev => ({
            ...prev,
            query: data.query,
            query_type: data.query_type,
            source_count: data.source_count,
            pending_sources: data.sources,
            remaining: data.source_count,
            initializing: false,
          }));
        } else if (data.type === 'result') {
          setSearchResults(prev => ({
            ...prev,
            results: [...prev.results, data.result],
            total_policies_found: prev.total_policies_found + (data.result.policies?.length || 0),
            remaining: data.remaining,
            pending_sources: prev.pending_sources?.filter(s => s.id !== data.result.source_id) || [],
            initializing: false,
          }));
        } else if (data.type === 'complete') {
          setSearchResults(prev => ({
            ...prev,
            searching: false,
            initializing: false,
            remaining: 0,
            pending_sources: [],
          }));
          setLoading(false);
          eventSource.close();
        }
      };

      eventSource.onerror = (err) => {
        console.error('SSE error:', err);
        eventSource.close();
        handleSearchFallback();
      };

    } catch (err) {
      handleSearchFallback();
    }
  };

  const handleSearchFallback = async () => {
    try {
      const response = await authFetch(
        `${API_BASE}/api/search?q=${encodeURIComponent(searchQuery)}&source=${selectedSource}`
      );
      const data = await response.json();
      setSearchResults({
        query: data.query,
        query_type: data.query_type,
        results: data.results,
        total_policies_found: data.total_policies_found,
        searching: false,
        initializing: false,
        remaining: 0,
        pending_sources: [],
      });
    } catch (err) {
      setError('Search failed. Please try again.');
    }
    setLoading(false);
  };

  const handlePolicyClick = async (policy) => {
    setSelectedPolicy(policy);
    setPolicyDetail(null);
    setDetailLoading(true);
    setResultsCollapsed(true); // give the detail panel the full width
    setRecentCollapsed(true);  // ...and tuck the Recent rail away too
    setClaimTimeOpen(false);
    setExpandedSections({});
    // Reset claims state when switching policies
    setClaimsData(null);
    setClaimsExpanded(false);
    setExpandedClaims({});
    setClaimDetails({});
    setClaimDetailLoading({});
    // Reset attachments state
    setAttachmentsData(null);
    setAttachmentsExpanded(false);
    setDownloadingAttachment(null);
    setScheduleLoading(false);
    // Reset extension metadata
    setExtensionNames({});
    setMetadataLoading(false);
    // Reset policy history
    liveDetailRef.current = null;
    setHistoryBanner(null);
    setHistoryError(null);
    setHistoryDate('');
    if (versionsEsRef.current) { versionsEsRef.current.close(); versionsEsRef.current = null; }
    setVersionsOpen(false);
    setVersions([]);
    setVersionsError(null);

    try {
      const response = await authFetch(
        `${API_BASE}/api/policy/${encodeURIComponent(policy.policy_number)}?source=${policy.source_id}`
      );
      const data = await response.json();
      setPolicyDetail(data);

      // Log the view after successfully loading details
      if (data.success) {
        logPolicyView(policy, data);

        // Preload claims in the background so the claims panel and the
        // per-claim history quick-links are ready without an extra wait.
        fetchClaims(policy.policy_number, policy.source_id);

        // Fire metadata fetch in background (non-blocking) for extension names
        if (data.bouquet && data.product && data.insurer_id) {
          setMetadataLoading(true);
          authFetch(
            `${API_BASE}/api/policy/metadata/extensions?source=${data.source_id}&bouquet=${data.bouquet}&product=${data.product}&insurer=${data.insurer_id}`
          )
            .then(res => res.json())
            .then(meta => {
              if (meta.success && meta.extensions) {
                setExtensionNames(meta.extensions);
              }
            })
            .catch(() => {})
            .finally(() => setMetadataLoading(false));
        }
      }
    } catch (err) {
      setError('Failed to load policy details.');
    }
    setDetailLoading(false);
  };

  // Fetch and show a historical snapshot (by date or by seq) — swaps it into
  // policyDetail (same shape) and stashes the live detail for restore.
  const loadHistorySnapshot = async (queryParam) => {
    if (!selectedPolicy) return;
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      const response = await authFetch(
        `${API_BASE}/api/policy/${encodeURIComponent(selectedPolicy.policy_number)}/history?source=${selectedPolicy.source_id}&${queryParam}`
      );
      const data = await response.json();
      if (!data || !data.success) {
        setHistoryError((data && data.error) || 'No policy version for that date.');
        setHistoryLoading(false);
        return;
      }
      if (!liveDetailRef.current) liveDetailRef.current = policyDetail;
      setPolicyDetail(data);
      setHistoryDate(data.as_at_date || '');
      setHistoryBanner({
        date: data.as_at_date,
        endorsement_date: data.endorsement_date,
        last_updated_date: data.last_updated_date,
        updated_reason: data.updated_reason,
        updated_by: data.updated_by,
        seq_no: data.seq_no,
        max_seq: data.max_seq,
      });
    } catch (err) {
      setHistoryError('Failed to load policy history.');
    }
    setHistoryLoading(false);
  };

  const handleHistoryPick = (dateStr) => {
    if (!dateStr) return;
    loadHistorySnapshot(`date=${encodeURIComponent(dateStr)}`);
  };

  const handleHistoryPickSeq = (seq) => {
    loadHistorySnapshot(`seq=${seq}`);
  };

  // Restore the live (current) policy detail.
  const exitHistory = () => {
    if (liveDetailRef.current) {
      setPolicyDetail(liveDetailRef.current);
      liveDetailRef.current = null;
    }
    setHistoryBanner(null);
    setHistoryError(null);
    setHistoryDate('');
  };

  // Version browser: stream the policy's version list newest-first.
  const openVersions = () => {
    if (!selectedPolicy) return;
    setVersions([]);
    setVersionsError(null);
    setVersionsMaxSeq(null);
    setVersionsLoading(true);
    setVersionsOpen(true);
    const token = getToken();
    const es = new EventSource(
      `${API_BASE}/api/policy/${encodeURIComponent(selectedPolicy.policy_number)}/versions/stream?source=${selectedPolicy.source_id}&token=${token}`
    );
    versionsEsRef.current = es;
    es.onmessage = (event) => {
      const d = JSON.parse(event.data);
      if (d.type === 'meta') {
        setVersionsMaxSeq(d.max_seq);
      } else if (d.type === 'version') {
        setVersions(prev => [...prev, d]);
      } else if (d.type === 'error') {
        setVersionsError(d.error || 'Failed to load versions.');
        setVersionsLoading(false);
        es.close();
      } else if (d.type === 'done') {
        setVersionsLoading(false);
        es.close();
      }
    };
    es.onerror = () => {
      setVersionsLoading(false);
      es.close();
    };
  };

  const closeVersions = () => {
    if (versionsEsRef.current) {
      versionsEsRef.current.close();
      versionsEsRef.current = null;
    }
    setVersionsOpen(false);
  };

  const pickVersion = (seq) => {
    closeVersions();
    handleHistoryPickSeq(seq);
  };

  // Handle clicking a recent view item
  const handleRecentClick = async (view) => {
    // Set the query in the search bar and trigger a full search
    setSearchQuery(view.policy_number);
    setSelectedSource(view.source_id);

    // Reset claims state
    setClaimsData(null);
    setClaimsExpanded(false);
    setExpandedClaims({});
    setClaimDetails({});
    setClaimDetailLoading({});

    // Trigger search using this policy number
    const sourcesToSearch = [{ id: view.source_id, name: view.source_name }];

    setLoading(true);
    setError(null);
    setSearchResults({
      query: view.policy_number,
      results: [],
      total_policies_found: 0,
      searching: true,
      initializing: true,
      remaining: sourcesToSearch.length,
      pending_sources: sourcesToSearch,
    });
    setSelectedPolicy(null);
    setPolicyDetail(null);
    setResultsCollapsed(false);
    setExpandedSections({});

    try {
      const token = getToken();
      const eventSource = new EventSource(
        `${API_BASE}/api/search/stream?q=${encodeURIComponent(view.policy_number)}&source=${view.source_id}&token=${token}`
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'start') {
          setSearchResults(prev => ({
            ...prev,
            query: data.query,
            query_type: data.query_type,
            source_count: data.source_count,
            pending_sources: data.sources,
            remaining: data.source_count,
            initializing: false,
          }));
        } else if (data.type === 'result') {
          setSearchResults(prev => ({
            ...prev,
            results: [...prev.results, data.result],
            total_policies_found: prev.total_policies_found + (data.result.policies?.length || 0),
            remaining: data.remaining,
            pending_sources: prev.pending_sources?.filter(s => s.id !== data.result.source_id) || [],
            initializing: false,
          }));
        } else if (data.type === 'complete') {
          setSearchResults(prev => ({
            ...prev,
            searching: false,
            initializing: false,
            remaining: 0,
            pending_sources: [],
          }));
          setLoading(false);
          eventSource.close();
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        // Fallback to non-streaming search
        authFetch(
          `${API_BASE}/api/search?q=${encodeURIComponent(view.policy_number)}&source=${view.source_id}`
        ).then(r => r.json()).then(data => {
          setSearchResults({ ...data, searching: false, pending_sources: [] });
          setLoading(false);
        }).catch(() => {
          setError('Search failed.');
          setLoading(false);
        });
      };
    } catch (err) {
      setError('Search failed.');
      setLoading(false);
    }
  };

  const toggleSection = (sectionType) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionType]: !prev[sectionType]
    }));
  };

  // Export policy as PDF
  // When viewing a historical version, exports fetch that exact version and the
  // filename is stamped so the file can never pass as the current policy.
  const historySeqParam = () => (historyBanner ? `&seq=${historyBanner.seq_no}` : '');
  const historyFileSuffix = () => {
    if (!historyBanner) return '';
    const d = historyBanner.endorsement_date || historyBanner.last_updated_date || '';
    return `_HISTORICAL_v${historyBanner.seq_no}${d ? `_${d}` : ''}`;
  };

  const handleExportPdf = async () => {
    if (!policyDetail || !selectedPolicy) return;

    setExporting('pdf');

    try {
      const response = await authFetch(
        `${API_BASE}/api/export/pdf/${encodeURIComponent(selectedPolicy.policy_number)}?source=${selectedPolicy.source_id}${historySeqParam()}`
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Policy_${policyDetail.display_policy_number || selectedPolicy.policy_number}${historyFileSuffix()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export PDF. Please try again.');
    }
    setExporting(null);
  };

  // Export policy as Excel
  const handleExportExcel = async () => {
    if (!policyDetail || !selectedPolicy) return;

    setExporting('excel');

    try {
      const response = await authFetch(
        `${API_BASE}/api/export/excel/${encodeURIComponent(selectedPolicy.policy_number)}?source=${selectedPolicy.source_id}${historySeqParam()}`
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Policy_${policyDetail.display_policy_number || selectedPolicy.policy_number}${historyFileSuffix()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export Excel. Please try again.');
    }
    setExporting(null);
  };

  // Export policy items as flat Excel rows
  const handleExportExcelRows = async () => {
    if (!policyDetail || !selectedPolicy) return;

    setExporting('excel-rows');

    try {
      const response = await authFetch(
        `${API_BASE}/api/export/excel-rows/${encodeURIComponent(selectedPolicy.policy_number)}?source=${selectedPolicy.source_id}${historySeqParam()}`
      );

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Policy_${policyDetail.display_policy_number || selectedPolicy.policy_number}${historyFileSuffix()}_Rows.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export Excel rows. Please try again.');
    }
    setExporting(null);
  };

  // Fetch attachments for a policy
  const fetchAttachments = async (policyNumber, sourceId, internalPolicyNo) => {
    if (!internalPolicyNo) return;
    setAttachmentsLoading(true);
    setAttachmentsData(null);
    try {
      const response = await authFetch(
        `${API_BASE}/api/policy/${encodeURIComponent(policyNumber)}/attachments?source=${sourceId}&policy_no=${internalPolicyNo}&fetch_all=true`
      );
      const data = await response.json();
      setAttachmentsData(data);
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
      setAttachmentsData({ success: false, error: 'Failed to load attachments' });
    }
    setAttachmentsLoading(false);
  };

  // Download a specific attachment
  const handleDownloadAttachment = async (attachment) => {
    if (!policyDetail || !selectedPolicy) return;
    setDownloadingAttachment(attachment.attachment_id);
    try {
      const response = await authFetch(
        `${API_BASE}/api/policy/${encodeURIComponent(selectedPolicy.policy_number)}/attachments/${attachment.attachment_id}?source=${selectedPolicy.source_id}`
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      setError('Failed to download attachment. Please try again.');
    }
    setDownloadingAttachment(null);
  };

  const handleDownloadSchedule = async () => {
    if (!policyDetail || !selectedPolicy) return;
    setScheduleLoading(true);
    try {
      const response = await authFetch(
        `${API_BASE}/api/policy/${encodeURIComponent(selectedPolicy.policy_number)}/document?source=${selectedPolicy.source_id}`
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || 'Failed to generate schedule');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedPolicy.policy_number}_PolicySchedule.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download policy schedule:', err);
      setError(err.message || 'Failed to download policy schedule. Please try again.');
    }
    setScheduleLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#22c55e';
      case 'degraded': return '#eab308';
      case 'offline': return '#ef4444';
      default: return '#64748b';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return '●';
      case 'degraded': return '●';
      default: return '○';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'R 0.00';
    return 'R ' + new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  const getTimeSinceLastCheck = () => {
    if (!lastChecked) return 'Never';
    const seconds = Math.floor((new Date() - lastChecked) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  const onlineCount = sources.filter(s => s.status === 'online').length;
  const offlineCount = sources.filter(s => s.status === 'offline').length;

  // Check if an item has extra detail data worth showing in the modal
  const itemHasExtraDetails = (item) => {
    const d = item.details || {};
    return !!(d.extensions?.length || d.deductibles?.length || d.endorsements?.length ||
              d.accessories?.length || d.note || d.finance_company);
  };

  const renderItemDetails = (item, itemType) => {
    const details = item.details || {};
    const idChips = [];
    const metaChips = [];

    // Row 1: Type-specific identification
    if (itemType === 'vehicle' || itemType === 'motorcycle' || itemType === 'caravan') {
      if (details.registration && details.registration !== 'TBA') {
        idChips.push(<span key="reg" className="detail-chip"><strong>Reg:</strong> {details.registration}</span>);
      }
      if (details.vin) {
        idChips.push(<span key="vin" className="detail-chip"><strong>VIN:</strong> {details.vin}</span>);
      }
      if (details.engine_number) {
        idChips.push(<span key="eng" className="detail-chip"><strong>Engine:</strong> {details.engine_number}</span>);
      }
      if (details.colour && details.colour !== 'Unknown') {
        idChips.push(<span key="col" className="detail-chip"><strong>Colour:</strong> {details.colour}</span>);
      }
      if (details.cubic_capacity && details.cubic_capacity !== '0') {
        idChips.push(<span key="cc" className="detail-chip"><strong>CC:</strong> {details.cubic_capacity}</span>);
      }
    }

    if ((itemType === 'building' || itemType === 'householder') && details.address && details.address !== item.description) {
      idChips.push(<span key="addr" className="detail-chip address">{details.address}</span>);
    }
    if (itemType === 'building' && details.bondholder) {
      idChips.push(<span key="bond" className="detail-chip"><strong>Bondholder:</strong> {details.bondholder}</span>);
    }

    if (itemType === 'allrisk') {
      if (details.serial_number) {
        idChips.push(<span key="sn" className="detail-chip"><strong>S/N:</strong> {details.serial_number}</span>);
      }
      if (details.model) {
        idChips.push(<span key="mod" className="detail-chip"><strong>Model:</strong> {details.model}</span>);
      }
    }

    if (itemType === 'watercraft') {
      if (details.registration) {
        idChips.push(<span key="reg" className="detail-chip"><strong>Reg:</strong> {details.registration}</span>);
      }
    }

    if (itemType === 'special_risk' && details.special_risk_info) {
      idChips.push(<span key="sri" className="detail-chip">{details.special_risk_info}</span>);
    }

    // Row 2: Metadata — dates, financials, claims
    if (details.inception_date) {
      metaChips.push(<span key="inc"><strong>Inception:</strong> {formatDate(details.inception_date)}</span>);
    }
    if (details.original_inception_date) {
      metaChips.push(<span key="orig"><strong>Original:</strong> {formatDate(details.original_inception_date)}</span>);
    }
    if (details.amended_date) {
      metaChips.push(<span key="amd"><strong>Amended:</strong> {formatDate(details.amended_date)}</span>);
    }
    if (details.sasria_premium) {
      metaChips.push(<span key="sas"><strong>SASRIA:</strong> {formatCurrency(details.sasria_premium)}</span>);
    }
    if (details.commission_pct) {
      metaChips.push(<span key="comm"><strong>Comm:</strong> {details.commission_amount ? `${formatCurrency(details.commission_amount)} (${details.commission_pct}%)` : `${details.commission_pct}%`}</span>);
    }
    if (details.ncb && details.ncb !== '0') {
      metaChips.push(<span key="ncb"><strong>NCB:</strong> {details.ncb}</span>);
    }
    if (details.claims_count && details.claims_count !== '0') {
      metaChips.push(<span key="clm"><strong>Claims:</strong> {details.claims_count}</span>);
    }

    const hasContent = idChips.length > 0 || metaChips.length > 0;
    if (!hasContent) return null;

    return (
      <div className="item-detail-extra">
        {idChips.length > 0 && <div className="item-id-row">{idChips}</div>}
        {metaChips.length > 0 && (
          <div className="item-meta-row">
            {metaChips}
          </div>
        )}
      </div>
    );
  };

  const renderItemModal = () => {
    if (!itemModal) return null;
    const { item, itemType } = itemModal;
    const details = item.details || {};

    return (
      <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setItemModal(null); }}>
        <div className="admin-modal admin-modal-wide item-modal" onClick={e => e.stopPropagation()}>
          <div className="admin-modal-header">
            <span className="item-modal-icon">{TYPE_ICONS[itemType] || '📦'}</span>
            <h3>{item.description}</h3>
            <button className="admin-modal-close" onClick={() => setItemModal(null)}>&times;</button>
          </div>
          <div className="admin-modal-body">
            {/* Premium Summary */}
            <div className="im-section">
              <div className="im-summary-grid">
                <div className="im-summary-item">
                  <span className="im-summary-label">Sum Insured</span>
                  <span className="im-summary-value green">{formatCurrency(item.sum_insured)}</span>
                </div>
                <div className="im-summary-item">
                  <span className="im-summary-label">Premium</span>
                  <span className="im-summary-value amber">{formatCurrency(item.premium)}/m</span>
                </div>
                {details.extension_premium > 0 && (
                  <div className="im-summary-item">
                    <span className="im-summary-label">Incl. Extensions</span>
                    <span className="im-summary-value blue">{formatCurrency(details.extension_premium)}/m</span>
                  </div>
                )}
                {details.accessory_premium > 0 && (
                  <div className="im-summary-item">
                    <span className="im-summary-label">Incl. Accessories</span>
                    <span className="im-summary-value blue">{formatCurrency(details.accessory_premium)}/m</span>
                  </div>
                )}
                {details.sasria_premium > 0 && (
                  <div className="im-summary-item">
                    <span className="im-summary-label">SASRIA</span>
                    <span className="im-summary-value">{formatCurrency(details.sasria_premium)}/m</span>
                  </div>
                )}
              </div>
            </div>

            {/* Deductibles / Excesses */}
            {details.deductibles?.length > 0 && (
              <div className="im-section">
                <h4 className="im-section-title">Excesses</h4>
                <table className="im-table">
                  <thead>
                    <tr><th>Description</th><th>Amount</th><th>%</th></tr>
                  </thead>
                  <tbody>
                    {details.deductibles.map((ded, i) => (
                      <tr key={i}>
                        <td>{ded.description || 'Standard'}</td>
                        <td>{ded.amount ? formatCurrency(ded.amount) : '-'}</td>
                        <td>{ded.percentage ? `${ded.percentage}%` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Extensions */}
            {details.extensions?.length > 0 && (
              <div className="im-section">
                <h4 className="im-section-title">Extensions</h4>
                {metadataLoading ? (
                  <div className="ext-loading">
                    <span className="pulsing-dots"><span>.</span><span>.</span><span>.</span></span>
                    {' '}Loading extension details
                  </div>
                ) : (
                <table className="im-table">
                  <thead>
                    <tr><th>Extension</th><th>Sum Insured</th><th>Premium</th></tr>
                  </thead>
                  <tbody>
                    {details.extensions.map((ext, i) => (
                      <tr key={i}>
                        <td>{extensionNames[ext.id] || ext.description || `Extension ${ext.id}`}</td>
                        <td>{formatCurrency(ext.sum_insured)}</td>
                        <td>{formatCurrency(ext.premium)}/m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
              </div>
            )}

            {/* Accessories */}
            {details.accessories?.length > 0 && (
              <div className="im-section">
                <h4 className="im-section-title">Accessories</h4>
                <table className="im-table">
                  <thead>
                    <tr><th>Description</th><th>Sum Insured</th><th>Premium</th></tr>
                  </thead>
                  <tbody>
                    {details.accessories.map((acc, i) => (
                      <tr key={i}>
                        <td>{acc.description}</td>
                        <td>{formatCurrency(acc.sum_insured)}</td>
                        <td>{formatCurrency(acc.premium)}/m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Endorsements */}
            {details.endorsements?.length > 0 && (
              <div className="im-section">
                <h4 className="im-section-title">Endorsements</h4>
                <div className="im-endorsements">
                  {details.endorsements.map((end, i) => (
                    <div key={i} className="im-endorsement-item">
                      <span className="im-endorsement-desc">{end.description}</span>
                      {end.wording && <span className="im-endorsement-wording">{end.wording}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Finance */}
            {details.finance_company && (
              <div className="im-section">
                <h4 className="im-section-title">Finance</h4>
                <p className="im-text">{details.finance_company}</p>
              </div>
            )}

            {/* Notes */}
            {details.note && (
              <div className="im-section">
                <h4 className="im-section-title">Notes</h4>
                <p className="im-note">{details.note}</p>
              </div>
            )}

            {/* Dates & Info */}
            <div className="im-section">
              <h4 className="im-section-title">Information</h4>
              <div className="im-info-grid">
                {details.inception_date && (
                  <div className="im-info-item">
                    <span className="im-info-label">Inception</span>
                    <span className="im-info-value">{formatDate(details.inception_date)}</span>
                  </div>
                )}
                {details.original_inception_date && (
                  <div className="im-info-item">
                    <span className="im-info-label">Original Inception</span>
                    <span className="im-info-value">{formatDate(details.original_inception_date)}</span>
                  </div>
                )}
                {details.amended_date && (
                  <div className="im-info-item">
                    <span className="im-info-label">Amended</span>
                    <span className="im-info-value">{formatDate(details.amended_date)}</span>
                  </div>
                )}
                {details.end_date && (
                  <div className="im-info-item">
                    <span className="im-info-label">End Date</span>
                    <span className="im-info-value">{formatDate(details.end_date)}</span>
                  </div>
                )}
                {details.commission_pct > 0 && (
                  <div className="im-info-item">
                    <span className="im-info-label">Commission</span>
                    <span className="im-info-value">{details.commission_pct}%{details.commission_amount ? ` (${formatCurrency(details.commission_amount)})` : ''}</span>
                  </div>
                )}
                {details.ncb && details.ncb !== '0' && (
                  <div className="im-info-item">
                    <span className="im-info-label">NCB</span>
                    <span className="im-info-value">{details.ncb}</span>
                  </div>
                )}
                {details.claims_count && details.claims_count !== '0' && (
                  <div className="im-info-item">
                    <span className="im-info-label">Claims</span>
                    <span className="im-info-value">{details.claims_count}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper to render the results header with proper state
  const renderResultsHeader = () => {
    if (!searchResults) return null;

    const { searching, initializing, total_policies_found, remaining, pending_sources } = searchResults;

    if (initializing) {
      return (
        <div className="results-header searching">
          <SearchSpinner />
          <span>Searching {pending_sources?.length || 0} source{(pending_sources?.length || 0) !== 1 ? 's' : ''}...</span>
        </div>
      );
    }

    if (searching) {
      const sourcesLeft = pending_sources?.length || remaining || 0;
      return (
        <div className="results-header searching">
          <SearchSpinner />
          <span>
            {total_policies_found > 0 
              ? `Found ${total_policies_found} polic${total_policies_found === 1 ? 'y' : 'ies'}`
              : 'Searching'
            }
            {sourcesLeft > 0 && (
              <span className="searching-status"> ({sourcesLeft} source{sourcesLeft !== 1 ? 's' : ''} remaining)</span>
            )}
          </span>
        </div>
      );
    }

    return (
      <div className="results-header">
        <span>Found {total_policies_found} polic{total_policies_found === 1 ? 'y' : 'ies'}</span>
      </div>
    );
  };

  // Render the recent views sidebar
  const renderRecentSidebar = () => {
    const groupedViews = groupViewsByTime(recentViews);
    const hasViews = recentViews.length > 0;
    
    return (
      <div className={`recent-sidebar ${recentCollapsed ? 'collapsed' : ''}`}>
        <div className="recent-header" onClick={() => setRecentCollapsed(!recentCollapsed)}>
          <div className="recent-header-left">
            <ClockIcon />
            <span className="recent-title">Recent</span>
            {hasViews && <span className="recent-count">{recentViews.length}</span>}
          </div>
          <ChevronIcon direction={recentCollapsed ? 'right' : 'left'} />
        </div>
        
        {!recentCollapsed && (
          <div className="recent-content">
            {recentLoading ? (
              <div className="recent-loading"><SearchSpinner /></div>
            ) : !hasViews ? (
              <div className="recent-empty">
                <span className="recent-empty-icon">📋</span>
                <span>No recent policies</span>
              </div>
            ) : (
              <div className="recent-list">
                {groupedViews.today.length > 0 && (
                  <div className="recent-group">
                    <div className="recent-group-label">Today</div>
                    {groupedViews.today.map(view => (
                      <div key={view.id} className={`recent-item ${selectedPolicy?.policy_number === view.policy_number ? 'active' : ''}`} onClick={() => handleRecentClick(view)}>
                        <div className="recent-item-policy">{view.policy_number}</div>
                        <div className="recent-item-client">{view.client_name || 'Unknown'}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-source">{view.source_name}</span>
                          <span className="recent-item-time">{formatTimeAgo(view.viewed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {groupedViews.yesterday.length > 0 && (
                  <div className="recent-group">
                    <div className="recent-group-label">Yesterday</div>
                    {groupedViews.yesterday.map(view => (
                      <div key={view.id} className={`recent-item ${selectedPolicy?.policy_number === view.policy_number ? 'active' : ''}`} onClick={() => handleRecentClick(view)}>
                        <div className="recent-item-policy">{view.policy_number}</div>
                        <div className="recent-item-client">{view.client_name || 'Unknown'}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-source">{view.source_name}</span>
                          <span className="recent-item-time">{formatTimeAgo(view.viewed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {groupedViews.thisWeek.length > 0 && (
                  <div className="recent-group">
                    <div className="recent-group-label">This Week</div>
                    {groupedViews.thisWeek.map(view => (
                      <div key={view.id} className={`recent-item ${selectedPolicy?.policy_number === view.policy_number ? 'active' : ''}`} onClick={() => handleRecentClick(view)}>
                        <div className="recent-item-policy">{view.policy_number}</div>
                        <div className="recent-item-client">{view.client_name || 'Unknown'}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-source">{view.source_name}</span>
                          <span className="recent-item-time">{formatTimeAgo(view.viewed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {groupedViews.older.length > 0 && (
                  <div className="recent-group">
                    <div className="recent-group-label">Older</div>
                    {groupedViews.older.map(view => (
                      <div key={view.id} className={`recent-item ${selectedPolicy?.policy_number === view.policy_number ? 'active' : ''}`} onClick={() => handleRecentClick(view)}>
                        <div className="recent-item-policy">{view.policy_number}</div>
                        <div className="recent-item-client">{view.client_name || 'Unknown'}</div>
                        <div className="recent-item-meta">
                          <span className="recent-item-source">{view.source_name}</span>
                          <span className="recent-item-time">{formatTimeAgo(view.viewed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // PUBLIC ROUTES (no auth required, bypass login gate)
  // ============================================
  // /forgot-password and /reset-password must be reachable without a
  // session — the reset flow starts from an email link sent to a user
  // who has, by definition, lost access to their account.

  if (location.pathname === '/forgot-password') {
    return <ForgotPasswordScreen />;
  }
  if (location.pathname === '/reset-password') {
    return <ResetPasswordScreen />;
  }

  // ============================================
  // RENDER LOGIN SCREEN IF NOT AUTHENTICATED
  // ============================================

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // ============================================
  // RENDER FORCED PASSWORD CHANGE SCREEN
  // ============================================

  if (mustChangePassword) {
    return (
      <ForceChangePasswordScreen
        user={user}
        onPasswordChanged={(updatedUser) => {
          setUser(updatedUser);
          setMustChangePassword(false);
          navigate('/', { replace: true });
        }}
      />
    );
  }

  // ============================================
  // RENDER CONNECTION LOADER
  // ============================================

  if (!connectionChecked) {
    return (
      <ConnectionLoader
        onComplete={() => setConnectionChecked(true)}
        checkHealth={checkHealth}
        sources={sources}
        healthLoading={healthLoading}
      />
    );
  }

  // ============================================
  // RENDER MAIN APP
  // ============================================

  return (
    <div className="app">
      {/* Top accent bar */}
      <div className="accent-bar"></div>

      {/* Persistent Velora corner mark */}
      <div className="velora-mark" aria-label="Velora">
        <span className="velora-mark-name">Velora</span>
        <span className="velora-mark-sep">·</span>
        <span className="velora-mark-year">© {new Date().getFullYear()}</span>
      </div>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-brand" onClick={() => { navigate('/'); setSearchResults(null); setSelectedPolicy(null); setPolicyDetail(null); setSearchQuery(''); fetchRecentViews(); }} style={{ cursor: 'pointer' }}>
            <HubNetworkLogo />
            <div className="brand-text">
              <h1 className="logo-text">PolicyLink</h1>
            </div>
          </div>
          
          <div className="header-right">
            {/* Nav links */}
            <AdminNavLink to="/" label="Search" />
            {user?.role === 'super_admin' && (
              <AdminNavLink to="/admin" label="Platform Admin" />
            )}
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <AdminNavLink to="/manage" label="Manage Users" />
            )}

            {/* Connections dropdown */}
            <div className="header-status" ref={dropdownRef}>
              <button 
                className={`status-button ${showConnections ? 'active' : ''}`}
                onClick={() => setShowConnections(!showConnections)}
              >
                {healthLoading ? (
                  <span className="status-loading">
                    <RefreshIcon spinning={true} />
                    <span>Checking...</span>
                  </span>
                ) : (
                  <>
                    <span className="status-indicators">
                      {onlineCount > 0 && (
                        <span className="status-badge online">
                          <span className="status-dot">●</span>
                          {onlineCount}
                        </span>
                      )}
                      {offlineCount > 0 && (
                        <span className="status-badge offline">
                          <span className="status-dot">○</span>
                          {offlineCount}
                        </span>
                      )}
                    </span>
                    <span className="status-label">
                      {sources.length} source{sources.length !== 1 ? 's' : ''}
                    </span>
                    <span className="status-chevron">{showConnections ? '▲' : '▼'}</span>
                  </>
                )}
              </button>
              
              {showConnections && (
                <div className="connections-dropdown">
                  <div className="connections-header">
                    <span className="connections-title">Connections</span>
                    <button 
                      className="refresh-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        checkHealth();
                      }}
                      disabled={healthLoading}
                    >
                      <RefreshIcon spinning={healthLoading} />
                      <span>Refresh</span>
                    </button>
                  </div>
                  
                  <div className="connections-list">
                    {sources.map(source => (
                      <div key={source.id} className={`connection-item ${source.status}`}>
                        <span 
                          className="connection-status"
                          style={{ color: getStatusColor(source.status) }}
                        >
                          {getStatusIcon(source.status)}
                        </span>
                        <span className="connection-name">{source.name}</span>
                        <span className="connection-time">
                          {source.status === 'online' && source.response_time_ms 
                            ? `${source.response_time_ms}ms`
                            : source.status === 'offline' 
                              ? 'Offline'
                              : source.status
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="connections-footer">
                    Last checked: {getTimeSinceLastCheck()}
                  </div>
                </div>
              )}
            </div>

            {/* Security info */}
            <button
              className="security-info-btn"
              onClick={() => setShowSecurityInfo(true)}
              title="Security & Data Protection"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* User menu */}
            <div className="user-menu-container" ref={userMenuRef}>
              <button 
                className="user-menu-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <span className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
                <span className="user-name">{user?.name || 'User'}</span>
                <span className="user-chevron">{showUserMenu ? '▲' : '▼'}</span>
              </button>

              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-name">{user?.name}</div>
                    <div className="user-dropdown-email">{user?.email}</div>
                    <div className="user-dropdown-tenant">{user?.tenant_name}</div>
                  </div>
                  <div className="user-dropdown-divider"></div>
                  <button className="user-dropdown-item" onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}>
                    Change Password
                  </button>
                  <button className="user-dropdown-item logout" onClick={handleLogout}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Security Info Modal */}
      {showSecurityInfo && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowSecurityInfo(false); }}>
          <div className="admin-modal security-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Security &amp; Data Protection</h3>
              <button className="admin-modal-close" onClick={() => setShowSecurityInfo(false)}>&times;</button>
            </div>
            <div className="admin-modal-body security-modal-body">
              <div className="security-item">
                <span className="security-check">&#10003;</span>
                <div>
                  <strong>Encrypted Credentials</strong>
                  <p>Source credentials encrypted at rest (Fernet/PBKDF2).</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-check">&#10003;</span>
                <div>
                  <strong>Password Hashing</strong>
                  <p>Bcrypt with per-user salts. Passwords are never stored in plain text.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-check">&#10003;</span>
                <div>
                  <strong>Role-Based Access</strong>
                  <p>User, Admin, and Super Admin roles with source-level permissions.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-check">&#10003;</span>
                <div>
                  <strong>Parameterized Queries</strong>
                  <p>All database queries are parameterized to prevent injection.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-check">&#10003;</span>
                <div>
                  <strong>Credential Isolation</strong>
                  <p>API credentials are decrypted server-side only and never returned in responses.</p>
                </div>
              </div>
              <div className="security-item">
                <span className="security-check">&#10003;</span>
                <div>
                  <strong>Tenant Separation</strong>
                  <p>Data is fully isolated between tenants at the query level.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/admin" element={user?.role === 'super_admin' ? <SuperAdmin /> : <Navigate to="/" replace />} />
        <Route path="/manage" element={(user?.role === 'admin' || user?.role === 'super_admin') ? <ClientAdmin /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route path="/" element={
      <main className={`main${searchResults === null && !loading && !detailLoading && !selectedPolicy ? ' hero-mode' : ''}`}>
        {/* Hero section — shown when no search active */}
        {searchResults === null && !loading && !detailLoading && !selectedPolicy && (
          <div className="hero-section">
            <div className="hero-greeting">
              <h1 className="hero-greeting-title">
                Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.
              </h1>
              <p className="hero-greeting-sub">
                Search policies across all your connected sources.
              </p>
            </div>

            <div className="hero-panel">
              <form onSubmit={handleSearch} className="search-form hero-search-form">
                <SourcePicker
                  sources={sources}
                  value={selectedSource}
                  onChange={setSelectedSource}
                  disabled={loading}
                  size="hero"
                />
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Policy no, ID, cell or email…"
                    className="search-input"
                    disabled={loading}
                    autoFocus
                  />
                  <button type="submit" className="search-button" disabled={loading || !searchQuery.trim()} aria-label="Search">
                    {loading ? <SearchSpinner /> : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2.25"/>
                        <path d="m20 20-3.4-3.4" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {searchQuery.trim() && (
                  <div className="search-type-chip">Searching by <strong>{classifySearch(searchQuery)}</strong></div>
                )}
              </form>

              {sources.length > 0 && (
                <div className="hero-source-status">
                  <div className="hero-source-status-summary">
                    <span className={`hero-status-dot ${sources.every(s => s.status === 'online') ? 'online' : 'mixed'}`} aria-hidden="true"></span>
                    <span>
                      {sources.filter(s => s.status === 'online').length} of {sources.length} {sources.length === 1 ? 'source' : 'sources'} online
                    </span>
                  </div>
                  {sources.some(s => s.status === 'offline' || s.status === 'degraded') && (
                    <div className="hero-source-problems">
                      {sources
                        .filter(s => s.status === 'offline' || s.status === 'degraded')
                        .map(s => (
                          <span key={s.id} className={`hero-source-chip ${s.status}`} title={s.status}>
                            <span className="hero-source-chip-dot" aria-hidden="true"></span>
                            {s.name}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {recentViews.length > 0 && (
                <div className="hero-recent">
                  <div className="hero-recent-header">
                    <ClockIcon />
                    <span>Recent</span>
                  </div>
                  <div className="hero-recent-list">
                    {recentViews.slice(0, 5).map(view => (
                      <div key={view.id} className="hero-recent-item" onClick={() => handleRecentClick(view)}>
                        <span className="hero-recent-policy">{view.policy_number}</span>
                        <span className="hero-recent-client">{view.client_name || 'Unknown'}</span>
                        <span className="hero-recent-source">{view.source_name}</span>
                        <span className="hero-recent-time">{formatTimeAgo(view.viewed_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compact search — shown when search is active */}
        {!(searchResults === null && !loading && !detailLoading && !selectedPolicy) && (
        <div className="search-wrapper">
          <form onSubmit={handleSearch} className="search-form">
            <SourcePicker
              sources={sources}
              value={selectedSource}
              onChange={setSelectedSource}
              disabled={loading}
              size="compact"
            />
            <div className="search-input-wrapper">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Policy no, ID, cell or email…"
                className="search-input"
                disabled={loading}
              />
              <button type="submit" className="search-button" disabled={loading || !searchQuery.trim()} aria-label="Search">
                {loading ? <SearchSpinner /> : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2.25"/>
                    <path d="m20 20-3.4-3.4" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            </div>
            {searchQuery.trim() && (
              <div className="search-type-chip">Searching by <strong>{classifySearch(searchQuery)}</strong></div>
            )}
          </form>
        </div>
        )}

        {error && (
          <div className="error-banner">
            {error}
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}

        <div className="content-with-sidebar">
          {/* Recent Views Sidebar */}
          {renderRecentSidebar()}
          
          <div className={`content${resultsCollapsed && selectedPolicy ? ' results-collapsed' : ''}`}>
            {/* Collapsed results rail — click to bring the list back */}
            {resultsCollapsed && selectedPolicy && (
              <button
                className="results-rail"
                onClick={() => setResultsCollapsed(false)}
                title="Show results list"
              >
                <span className="results-rail-chevron" aria-hidden="true">›</span>
                <span className="results-rail-label">
                  Results{typeof searchResults?.total_policies_found === 'number' ? ` · ${searchResults.total_policies_found}` : ''}
                </span>
              </button>
            )}
            {/* Results List */}
            {!(resultsCollapsed && selectedPolicy) && (
            <div className="results-panel">
              {selectedPolicy && (
                <button
                  className="results-collapse-btn"
                  onClick={() => setResultsCollapsed(true)}
                  title="Hide results to widen the policy view"
                >
                  ‹ Hide
                </button>
              )}
              {searchResults && (
                <>
                  {renderResultsHeader()}

                {/* Pending sources (still searching) - sorted alphabetically */}
                {searchResults.pending_sources?.length > 0 && (
                  <div className="pending-sources-list">
                    {[...searchResults.pending_sources]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(source => (
                      <div key={source.id} className="source-result pending">
                        <div className="source-result-header">
                          <span className="source-status-dot searching">
                            <span className="pulse-dot"></span>
                          </span>
                          <span className="source-result-name">{source.name}</span>
                          <span className="source-result-time searching">searching...</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Completed source results - sorted: hits first, then alphabetical */}
                {[...searchResults.results]
                  .sort((a, b) => {
                    // Sources with policies come first
                    const aHasHits = a.policies?.length > 0 ? 1 : 0;
                    const bHasHits = b.policies?.length > 0 ? 1 : 0;
                    if (bHasHits !== aHasHits) return bHasHits - aHasHits;
                    // Then alphabetical by source name
                    return a.source_name.localeCompare(b.source_name);
                  })
                  .map(sourceResult => (
                  <div key={sourceResult.source_id} className="source-result">
                    <div className="source-result-header">
                      <span className="source-status-dot" style={{ color: getStatusColor(sourceResult.status === 'success' || sourceResult.status === 'no_results' ? 'online' : 'offline') }}>●</span>
                      <span className="source-result-name">{sourceResult.source_name}</span>
                      {sourceResult.response_time_ms && (
                        <span className="source-result-time">{sourceResult.response_time_ms}ms</span>
                      )}
                    </div>

                    {sourceResult.status === 'error' && (
                      <div className="source-error">{sourceResult.error}</div>
                    )}

                    {sourceResult.status === 'no_results' && (
                      <div className="source-no-results">No policies found</div>
                    )}

                    {sourceResult.status === 'success' && sourceResult.policies.map(policy => (
                      <div
                        key={`${policy.source_id}-${policy.policy_number}`}
                        className={`policy-card ${selectedPolicy?.policy_number === policy.policy_number ? 'selected' : ''}`}
                        onClick={() => handlePolicyClick(policy)}
                      >
                        <div className="policy-card-header">
                          <span className="policy-number">{policy.policy_number}</span>
                          <span className={`policy-status ${policy.status.toLowerCase().replace(' ', '-')}`}>
                            {policy.status}
                          </span>
                        </div>
                        <div className="policy-client">{sourceResult.client_name}</div>
                        <div className="policy-meta">
                          <span>{policy.package_type}</span>
                          {policy.outstanding_balance !== 0 && (
                            <div className={`policy-balance ${policy.outstanding_balance < 0 ? 'credit' : 'outstanding'}`}>
                              <span className="balance-label">
                                {policy.outstanding_balance < 0 ? 'Credit balance:' : 'Outstanding balance:'}
                              </span>
                              <span className="balance-amount">
                                {formatCurrency(Math.abs(policy.outstanding_balance))}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="policy-items-preview">
                          {policy.items.slice(0, 3).map((item, i) => (
                            <span key={i} className="item-tag">{item.coi}</span>
                          ))}
                          {policy.items.length > 3 && (
                            <span className="item-tag more">+{policy.items.length - 3}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}

            {!searchResults && !loading && (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h3>Search for policies</h3>
                <p>Enter a policy number</p>
              </div>
            )}
          </div>
          )}

          {/* Detail Panel */}
          <div className={`detail-panel${historyBanner ? ' history-active' : ''}`}>
            {historyLoading && (
              <div className="history-loading-overlay">
                <div className="spinner"></div>
                <p>Loading policy version…</p>
                <span className="history-loading-sub">Retrieving from the policy admin system — this can take a few seconds.</span>
              </div>
            )}
            {detailLoading && (
              <div className="loading">
                <div className="spinner"></div>
                <p>Loading policy details...</p>
              </div>
            )}

            {policyDetail && !detailLoading && policyDetail.success && (
              <>
                <div className="detail-sticky-header">
                  <div className="detail-header">
                    <div className="detail-header-left">
                      <h2>{policyDetail.display_policy_number || policyDetail.policy_number}</h2>
                      {policyDetail.client && <span className="client-name">{policyDetail.client.name}</span>}
                    </div>
                    {historyBanner ? (
                      <span className="policy-status historical">HISTORICAL</span>
                    ) : (
                      <span className="policy-status in-force">{policyDetail.status || 'In Force'}</span>
                    )}
                  </div>
                  <div className="detail-export-bar">
                    <span className="export-label">
                      <DownloadIcon />
                      {historyBanner ? 'Export (historical)' : 'Export'}
                    </span>
                    <div className="export-buttons">
                      {historyBanner ? (
                        <button
                          className="export-button schedule"
                          disabled
                          title="The Schedule is generated by the policy admin system and is only available for the current version"
                        >
                          <PdfIcon />
                          <span>Schedule (current only)</span>
                        </button>
                      ) : (
                      <button
                        className="export-button schedule"
                        onClick={handleDownloadSchedule}
                        disabled={scheduleLoading || exporting !== null}
                        title="Download Policy Schedule"
                      >
                        {scheduleLoading ? <SearchSpinner /> : <PdfIcon />}
                        <span>Schedule</span>
                      </button>
                      )}
                      <button
                        className="export-button pdf"
                        onClick={handleExportPdf}
                        disabled={exporting !== null}
                        title="Export PDF Summary"
                      >
                        {exporting === 'pdf' ? <SearchSpinner /> : <PdfIcon />}
                        <span>PDF Summary</span>
                      </button>
                      <button
                        className="export-button excel"
                        onClick={handleExportExcel}
                        disabled={exporting !== null}
                        title="Export Excel Summary"
                      >
                        {exporting === 'excel' ? <SearchSpinner /> : <ExcelIcon />}
                        <span>Excel Summary</span>
                      </button>
                      <button
                        className="export-button excel"
                        onClick={handleExportExcelRows}
                        disabled={exporting !== null}
                        title="Export Excel Rows"
                      >
                        {exporting === 'excel-rows' ? <SearchSpinner /> : <ExcelIcon />}
                        <span>Excel Rows</span>
                      </button>
                    </div>
                  </div>
                  <div className="detail-history-bar">
                    <div className="history-primary-row">
                      <span className="history-label">History</span>
                      <div className="history-controls">
                        <label className="history-date-field" title="View the policy as it stood on this date">
                          <span className="history-asat">As at</span>
                          <input
                            type="date"
                            className="history-date-input"
                            max={todayISO()}
                            value={historyDate}
                            onChange={(e) => handleHistoryPick(e.target.value)}
                          />
                        </label>
                        {liveDetail?.inception_date && (
                          <button className="history-chip" onClick={() => handleHistoryPick(liveDetail.inception_date)}>At inception</button>
                        )}
                        {liveDetail?.renewal_date && dayBefore(liveDetail.renewal_date) && (
                          <button className="history-chip" onClick={() => handleHistoryPick(dayBefore(liveDetail.renewal_date))}>Before renewal</button>
                        )}
                        {liveDetail?.last_updated_date && (
                          <button className="history-chip" onClick={() => handleHistoryPick(liveDetail.last_updated_date)}>Last change</button>
                        )}
                        <button className="history-chip browse" onClick={openVersions}>Browse versions…</button>
                        {historyBanner && (
                          <button className="history-chip current" onClick={exitHistory}>← Back to current</button>
                        )}
                      </div>
                    </div>

                    {/* Claims grouped together — each jumps to the policy as it stood on that claim's date of loss */}
                    {(() => {
                      const claimJumps = (claimsData?.success && claimsData?.has_claims && claimsData.claims)
                        ? [...claimsData.claims]
                            .filter(c => toISODate(c.date_of_loss))
                            .sort((a, b) => new Date(b.date_of_loss) - new Date(a.date_of_loss))
                        : [];
                      if (!claimJumps.length) return null;
                      return (
                        <div className="history-claimtime">
                          <button
                            type="button"
                            className={`history-claimtime-toggle ${claimTimeOpen ? 'open' : ''}`}
                            onClick={() => setClaimTimeOpen(o => !o)}
                            aria-expanded={claimTimeOpen}
                          >
                            <span className="expand-icon">{claimTimeOpen ? '▼' : '▶'}</span>
                            <span className="claimtime-flag" aria-hidden="true">⚑</span>
                            <span className="claimtime-title">Policy at claim time</span>
                            <span className="claimtime-count">{claimJumps.length}</span>
                            <span className="claimtime-hint">The policy as it stood on each claim's date of loss</span>
                          </button>
                          {claimTimeOpen && (
                            <div className="history-claimtime-chips">
                              {claimJumps.map(c => {
                                const iso = toISODate(c.date_of_loss);
                                const active = historyBanner && historyDate === iso;
                                return (
                                  <button
                                    key={c.claim_number}
                                    className={`history-chip claim ${active ? 'active' : ''}`}
                                    onClick={() => handleHistoryPick(iso)}
                                    title={`Claim ${c.claim_number} — date of loss ${formatDate(c.date_of_loss)}`}
                                  >
                                    <span className="claim-chip-date">{iso}</span>
                                    <span className="claim-chip-ref">{c.claim_number}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {historyError && <div className="history-error">{historyError}</div>}
                  {historyBanner && (
                    <div className="history-banner">
                      <span className="history-banner-tag">HISTORICAL VIEW</span>
                      <span className="history-banner-text">
                        <strong>Version {historyBanner.seq_no}{historyBanner.max_seq ? ` of ${historyBanner.max_seq}` : ''}</strong>
                        {historyBanner.endorsement_date && <> · effective <strong>{formatDate(historyBanner.endorsement_date)}</strong></>}
                        {historyBanner.last_updated_date && <> · recorded {formatDate(historyBanner.last_updated_date)}{historyBanner.updated_by ? ` by ${historyBanner.updated_by}` : ''}</>}
                        {historyBanner.updated_reason && <span className="history-reason"> — {historyBanner.updated_reason}</span>}
                      </span>
                    </div>
                  )}
                </div>

                <div className="detail-scroll-content">
                {policyDetail.client && (
                  <div className={`detail-section ${collapsedDetailSections.client ? 'collapsed' : ''}`}>
                    <h3 className="detail-section-toggle" onClick={() => toggleDetailSection('client')}>
                      Client
                      <span className="detail-collapse-icon" aria-hidden="true">{collapsedDetailSections.client ? '▸' : '▾'}</span>
                    </h3>
                    {!collapsedDetailSections.client && (
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>{policyDetail.client.is_commercial === true ? 'Reg Number' : 'ID Number'}</label>
                        <span>{policyDetail.client.id_number || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Cell</label>
                        <span>{policyDetail.client.cell || '-'}</span>
                      </div>
                      {policyDetail.client.email && (
                        <div className="detail-item full-width">
                          <label>Email</label>
                          <span>{policyDetail.client.email}</span>
                        </div>
                      )}
                      <div className="detail-item full-width">
                        <label>Address</label>
                        <span>{policyDetail.client.physical_address || '-'}</span>
                      </div>
                    </div>
                    )}
                  </div>
                )}

                <div className={`detail-section ${collapsedDetailSections.policy ? 'collapsed' : ''}`}>
                  <h3 className="detail-section-toggle" onClick={() => toggleDetailSection('policy')}>
                    Policy
                    <span className="detail-collapse-icon" aria-hidden="true">{collapsedDetailSections.policy ? '▸' : '▾'}</span>
                  </h3>
                  {!collapsedDetailSections.policy && (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Total Premium</label>
                      <span className="premium">{formatCurrency(policyDetail.total_premium)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Total Sum Insured</label>
                      <span className="sum-insured">{formatCurrency(policyDetail.total_sum_insured)}</span>
                    </div>
                    {policyDetail.total_commission > 0 && (
                    <div className="detail-item">
                      <label>Total Commission</label>
                      <span className="commission">{formatCurrency(policyDetail.total_commission)}</span>
                    </div>
                    )}
                    {policyDetail.insurer_name && (
                      <div className="detail-item">
                        <label>Insurer</label>
                        <span>{policyDetail.insurer_name}</span>
                      </div>
                    )}
                    {policyDetail.broker_name && (
                      <div className="detail-item">
                        <label>Broker</label>
                        <span>{policyDetail.broker_name}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <label>Source</label>
                      <span>{policyDetail.source_name}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Method</label>
                      <span>{policyDetail.payment_method}</span>
                    </div>
                    <div className="detail-item">
                      <label>Frequency</label>
                      <span>{policyDetail.payment_frequency}</span>
                    </div>
                    {policyDetail.debit_order_day && (
                      <div className="detail-item">
                        <label>Debit Day</label>
                        <span>{policyDetail.debit_order_day}</span>
                      </div>
                    )}
                    {policyDetail.inception_date && (
                      <div className="detail-item">
                        <label>Inception</label>
                        <span>{formatDate(policyDetail.inception_date)}</span>
                      </div>
                    )}
                    {policyDetail.original_inception_date && (
                      <div className="detail-item">
                        <label>Original Inception</label>
                        <span>{formatDate(policyDetail.original_inception_date)}</span>
                      </div>
                    )}
                    {policyDetail.expiry_date && (
                      <div className="detail-item">
                        <label>Expiry</label>
                        <span>{formatDate(policyDetail.expiry_date)}</span>
                      </div>
                    )}
                    {policyDetail.renewal_date && (
                      <div className="detail-item">
                        <label>Renewal</label>
                        <span>{formatDate(policyDetail.renewal_date)}</span>
                      </div>
                    )}
                    {policyDetail.review_date && (
                      <div className="detail-item">
                        <label>Review</label>
                        <span>{formatDate(policyDetail.review_date)}</span>
                      </div>
                    )}
                    {policyDetail.endorsement_date && (
                      <div className="detail-item">
                        <label>Endorsement</label>
                        <span>{formatDate(policyDetail.endorsement_date)}</span>
                      </div>
                    )}
                    {policyDetail.takeover_date && (
                      <div className="detail-item">
                        <label>Takeover</label>
                        <span>{formatDate(policyDetail.takeover_date)}</span>
                      </div>
                    )}
                    {policyDetail.file_no && (
                      <div className="detail-item">
                        <label>File No</label>
                        <span>{policyDetail.file_no}</span>
                      </div>
                    )}
                    {policyDetail.broker_ref_no && (
                      <div className="detail-item">
                        <label>Broker Ref</label>
                        <span>{policyDetail.broker_ref_no}</span>
                      </div>
                    )}
                  </div>
                  )}
                </div>

                {policyDetail.items_by_type && Object.keys(policyDetail.items_by_type).length > 0 && (
                  <div className="detail-section">
                    <div className="items-header">
                      <h3>Items ({policyDetail.item_count})</h3>
                      <span className="items-sum-label">Sum Insured</span>
                    </div>
                    <div className="items-accordion">
                      {Object.entries(policyDetail.items_by_type).map(([itemType, group]) => (
                        <div key={itemType} className="item-group">
                          <div
                            className={`item-group-header ${expandedSections[itemType] ? 'expanded' : ''}`}
                            onClick={() => toggleSection(itemType)}
                          >
                            <div className="item-group-left">
                              <span className="expand-icon">{expandedSections[itemType] ? '▼' : '▶'}</span>
                              <span className="item-type-icon">{TYPE_ICONS[itemType] || '📦'}</span>
                              <span className="item-group-name">{group.display_name}</span>
                              <span className="item-group-count">({group.count})</span>
                            </div>
                            <div className="item-group-totals">
                              {group.total_premium > 0 && (
                                <>
                                  <span className="item-group-premium">Premium: {formatCurrency(group.total_premium)}</span>
                                  <span className="item-group-separator">|</span>
                                </>
                              )}
                              <span className="item-group-sum">{formatCurrency(group.total_sum_insured)}</span>
                            </div>
                          </div>

                          {expandedSections[itemType] && (
                            <div className="item-group-items">
                              {group.items.map((item, idx) => (
                                <div key={idx} className="item-detail">
                                  <div className="item-detail-main">
                                    <span className="item-number">{idx + 1}.</span>
                                    <span className="item-description">{item.description}</span>
                                  </div>
                                  {renderItemDetails(item, itemType)}
                                  <div className="item-footer-row">
                                    {item.premium > 0 && (
                                      <span className="item-premium">Premium: {formatCurrency(item.premium)}</span>
                                    )}
                                    <span className="item-footer-right">
                                      {itemHasExtraDetails(item) && (
                                        <button className="item-details-btn" onClick={(e) => { e.stopPropagation(); setItemModal({ item, itemType }); }}>
                                          Details
                                        </button>
                                      )}
                                      <span className="item-sum">{formatCurrency(item.sum_insured)}</span>
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Claims Section - only visible if user has claims permission */}
                {user?.can_view_claims !== false && (
                <div className="detail-section claims-section">
                  <div
                    className={`claims-header ${claimsExpanded ? 'expanded' : ''}`}
                    onClick={() => {
                      if (!claimsExpanded && !claimsData && !claimsLoading) {
                        fetchClaims(policyDetail.policy_number, selectedPolicy.source_id);
                      }
                      setClaimsExpanded(!claimsExpanded);
                    }}
                  >
                    <div className="claims-header-left">
                      <span className="expand-icon">{claimsExpanded ? '▼' : '▶'}</span>
                      <ClaimsIcon />
                      <h3>Claims</h3>
                      {claimsData?.has_claims && (
                        <span className="claims-count">({claimsData.claims_count})</span>
                      )}
                    </div>
                    <div className="claims-header-right">
                      {claimsLoading && <SearchSpinner />}
                      {claimsData?.has_claims && claimsData.experience && (
                        <span className="claims-total">
                          Total: {formatCurrency(claimsData.experience.total_claim)}
                        </span>
                      )}
                    </div>
                  </div>

                  {claimsExpanded && (
                    <div className="claims-content">
                      {claimsLoading && (
                        <div className="claims-loading">
                          <SearchSpinner />
                          <span>Loading claims...</span>
                        </div>
                      )}

                      {claimsData && !claimsLoading && !claimsData.success && (
                        <div className="claims-error">
                          <span>Failed to load claims: {claimsData.error}</span>
                        </div>
                      )}

                      {claimsData && !claimsLoading && claimsData.success && !claimsData.has_claims && (
                        <div className="claims-empty">
                          <span>No claims on record for this policy</span>
                        </div>
                      )}

                      {claimsData && !claimsLoading && claimsData.success && claimsData.has_claims && (
                        <>
                          {/* Claims Experience Summary */}
                          {claimsData.experience && (
                            <div className="claims-experience">
                              <div className="experience-grid">
                                <div className="experience-item">
                                  <label>Total Claims</label>
                                  <span>{claimsData.claims_count}</span>
                                </div>
                                <div className="experience-item">
                                  <label>Total Estimated</label>
                                  <span>{formatCurrency(claimsData.experience.estimate_total)}</span>
                                </div>
                                <div className="experience-item">
                                  <label>Total Paid</label>
                                  <span className="paid">{formatCurrency(claimsData.experience.payments_total)}</span>
                                </div>
                                <div className="experience-item">
                                  <label>Outstanding</label>
                                  <span className="outstanding">{formatCurrency(claimsData.experience.outstanding_estimate_total)}</span>
                                </div>
                                {claimsData.experience.loss_ratio > 0 && (
                                  <div className="experience-item">
                                    <label>Loss Ratio</label>
                                    <span className={claimsData.experience.loss_ratio > 100 ? 'high-ratio' : ''}>
                                      {claimsData.experience.loss_ratio.toFixed(1)}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Individual Claims */}
                          <div className="claims-list">
                            {[...claimsData.claims].sort((a, b) => {
                              const dateA = new Date(a.date_of_loss || '1900-01-01');
                              const dateB = new Date(b.date_of_loss || '1900-01-01');
                              return dateB - dateA;
                            }).map((claim) => {
                              const detail = claimDetails[claim.claim_number];
                              const isLoadingDetail = claimDetailLoading[claim.claim_number];
                              // Use detail financials when summary has zeros but detail has real data
                              const totalClaim = (claim.total_claim === 0 && detail?.total_claim > 0) ? detail.total_claim : claim.total_claim;
                              const estimateTotal = (claim.estimate_total === 0 && detail?.estimate_total > 0) ? detail.estimate_total : claim.estimate_total;
                              const paymentsTotal = (claim.payments_total === 0 && detail?.payments_total > 0) ? detail.payments_total : claim.payments_total;
                              const outstandingTotal = (claim.outstanding_estimate_total === 0 && detail?.outstanding_estimate_total !== undefined && detail.outstanding_estimate_total !== 0) ? detail.outstanding_estimate_total : claim.outstanding_estimate_total;
                              const recoveriesTotal = (claim.recoveries_total === 0 && detail?.recoveries_total > 0) ? detail.recoveries_total : claim.recoveries_total;
                              const salvagesTotal = (claim.salvages_total === 0 && detail?.salvages_total > 0) ? detail.salvages_total : claim.salvages_total;
                              const dateReported = claim.date_reported || detail?.date_reported;
                              return (
                              <div key={claim.claim_number} className="claim-card">
                                <div
                                  className={`claim-card-header ${expandedClaims[claim.claim_number] ? 'expanded' : ''}`}
                                  onClick={() => toggleClaimExpanded(claim.claim_number)}
                                >
                                  <div className="claim-header-left">
                                    <span className="expand-icon">{expandedClaims[claim.claim_number] ? '▼' : '▶'}</span>
                                    <span className="claim-number">{claim.claim_number}</span>
                                    <span
                                      className="claim-status-badge"
                                      style={{ backgroundColor: CLAIM_STATUS_COLORS[claim.status] || '#6b7280' }}
                                    >
                                      {claim.status_description}
                                    </span>
                                  </div>
                                  <div className="claim-header-right">
                                    <span className="claim-amount">{formatCurrency(totalClaim)}</span>
                                  </div>
                                </div>

                                <div className="claim-card-summary">
                                  <div className="claim-dates">
                                    <span><strong>Date of Loss:</strong> {formatDate(claim.date_of_loss) || '-'}</span>
                                    <span><strong>Reported:</strong> {formatDate(dateReported) || '-'}</span>
                                  </div>
                                  {claim.description && (
                                    <div className="claim-description">{claim.description}</div>
                                  )}
                                </div>

                                {expandedClaims[claim.claim_number] && (() => {
                                  return (
                                  <div className="claim-card-details">
                                    {/* Loading indicator */}
                                    {isLoadingDetail && (
                                      <div className="claim-detail-loading">Loading claim details...</div>
                                    )}

                                    {/* Claim description from detail */}
                                    {detail?.description && (
                                      <div className="claim-detail-description">{detail.description}</div>
                                    )}

                                    {/* Claim Info */}
                                    <div className="claim-info-grid">
                                      {detail?.claims_handler && (
                                        <div className="claim-info-item">
                                          <label>Handler</label>
                                          <span>{detail.claims_handler}</span>
                                        </div>
                                      )}
                                      {(detail?.insurer_claim_no || claim.insurer_claim_no) && (
                                        <div className="claim-info-item">
                                          <label>Insurer Ref</label>
                                          <span>{detail?.insurer_claim_no || claim.insurer_claim_no}</span>
                                        </div>
                                      )}
                                      {(detail?.third_party_involved || claim.third_party_involved) && (
                                        <div className="claim-info-item">
                                          <label>Third Party</label>
                                          <span>Yes</span>
                                        </div>
                                      )}
                                      {(detail?.reported_to_police || claim.reported_to_police) && (
                                        <>
                                          <div className="claim-info-item">
                                            <label>Police Station</label>
                                            <span>{detail?.police_station || claim.police_station || '-'}</span>
                                          </div>
                                          <div className="claim-info-item">
                                            <label>Case No</label>
                                            <span>{detail?.case_no || claim.case_no || '-'}</span>
                                          </div>
                                        </>
                                      )}
                                      {detail?.loss_address && (
                                        <div className="claim-info-item" style={{ gridColumn: '1 / -1' }}>
                                          <label>Loss Address</label>
                                          <span>{detail.loss_address}</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Claim Financial Summary */}
                                    <div className="claim-financials">
                                      <div className="financial-row">
                                        <span className="financial-label">Estimated:</span>
                                        <span className="financial-value">{formatCurrency(estimateTotal)}</span>
                                      </div>
                                      <div className="financial-row">
                                        <span className="financial-label">Paid:</span>
                                        <span className="financial-value paid">{formatCurrency(paymentsTotal)}</span>
                                      </div>
                                      {recoveriesTotal > 0 && (
                                        <div className="financial-row">
                                          <span className="financial-label">Recovered:</span>
                                          <span className="financial-value recovered">{formatCurrency(recoveriesTotal)}</span>
                                        </div>
                                      )}
                                      {salvagesTotal > 0 && (
                                        <div className="financial-row">
                                          <span className="financial-label">Salvage:</span>
                                          <span className="financial-value">{formatCurrency(salvagesTotal)}</span>
                                        </div>
                                      )}
                                      <div className="financial-row outstanding-row">
                                        <span className="financial-label">Outstanding:</span>
                                        <span className="financial-value outstanding">{formatCurrency(outstandingTotal)}</span>
                                      </div>
                                    </div>

                                    {/* Claim Items - use detail items if available, fallback to summary */}
                                    {(() => {
                                      const items = detail?.items || claim.items;
                                      return items && items.length > 0 && (
                                      <div className="claim-items">
                                        <div className="claim-items-header">Items Claimed</div>
                                        {items.map((item, idx) => (
                                          <div key={idx} className="claim-item">
                                            <div className="claim-item-main">
                                              {item.coi_description && !/^(COI\s*)?\d+$/.test(item.coi_description.trim()) && (
                                                <span className="claim-item-coi">{item.coi_description}</span>
                                              )}
                                              {item.asset_description && (
                                                <span className="claim-item-asset">{item.asset_description}</span>
                                              )}
                                              {item.peril && (
                                                <span className="claim-item-peril"><span className="claim-item-label">Peril:</span> {item.peril}</span>
                                              )}
                                              {item.cause && (
                                                <span className="claim-item-cause"><span className="claim-item-label">Cause:</span> {item.cause}</span>
                                              )}
                                              {!item.peril && !item.cause && !item.asset_description && item.description && item.description !== item.coi_description && (
                                                <span className="claim-item-desc">{item.description}</span>
                                              )}
                                            </div>
                                            <div className="claim-item-amounts">
                                              <span className="claim-item-est">Est: {formatCurrency(item.estimate_total)}</span>
                                              <span className="claim-item-paid">Paid: {formatCurrency(item.payments_total)}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      );
                                    })()}

                                    {/* Claim documents (from the policy-level attachments fetch) */}
                                    {(() => {
                                      const claimDocs = (attachmentsData?.attachments || []).filter(
                                        a => a.display_claim_no && a.display_claim_no === claim.claim_number
                                      );
                                      if (attachmentsLoading && !attachmentsData) {
                                        return (
                                          <div className="claim-docs">
                                            <div className="claim-items-header">Documents</div>
                                            <div className="attachments-loading"><SearchSpinner /><span>Loading documents...</span></div>
                                          </div>
                                        );
                                      }
                                      if (claimDocs.length === 0) return null;
                                      return (
                                        <div className="claim-docs">
                                          <div className="claim-items-header">Documents ({claimDocs.length})</div>
                                          {claimDocs.map((att) => (
                                            <div key={att.attachment_id} className="attachment-item">
                                              <div className="attachment-info">
                                                <span className="attachment-filename">{att.file_name}</span>
                                                <span className="attachment-desc">
                                                  {att.description || att.type_description}
                                                  {att.is_confidential && <span className="attachment-confidential"> (Confidential)</span>}
                                                </span>
                                              </div>
                                              <button
                                                className="attachment-download-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownloadAttachment(att);
                                                }}
                                                disabled={downloadingAttachment !== null}
                                                title={`Download ${att.file_name}`}
                                              >
                                                {downloadingAttachment === att.attachment_id ? (
                                                  <SearchSpinner />
                                                ) : (
                                                  <DownloadIcon />
                                                )}
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}

                                    {/* Transaction History */}
                                    {detail?.items && detail.items.some(i => i.transactions && i.transactions.length > 0) && (
                                      <div className="claim-items">
                                        <div className="claim-items-header">Transaction History</div>
                                        {detail.items.flatMap((item, iIdx) =>
                                          (item.transactions || []).map((trans, tIdx) => (
                                            <div key={`${iIdx}-${tIdx}`} className="claim-transaction">
                                              <div className="claim-transaction-left">
                                                <span className="claim-transaction-type">{trans.transaction_type}</span>
                                                <span className="claim-transaction-date">{formatDate(trans.date)}</span>
                                              </div>
                                              <span className="claim-transaction-amount">{formatCurrency(trans.amount)}</span>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    )}

                                    {/* Activity History */}
                                    {detail?.activities && detail.activities.length > 0 && (
                                      <div className="claim-items">
                                        <div className="claim-items-header">Activity History</div>
                                        <div className="claim-activities">
                                          {detail.activities.map((activity, idx) => (
                                            <div key={idx} className="claim-activity">
                                              <div className="claim-activity-header">
                                                <span className="claim-activity-date">{formatDate(activity.date)}</span>
                                                <span className="claim-activity-user">{activity.username}</span>
                                              </div>
                                              <div className="claim-activity-note">{activity.note}</div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  );
                                })()}
                              </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Attachments Section */}
                <div className="detail-section attachments-section">
                  <div
                    className={`attachments-header ${attachmentsExpanded ? 'expanded' : ''}`}
                    onClick={() => {
                      if (!attachmentsExpanded && !attachmentsData && !attachmentsLoading) {
                        fetchAttachments(
                          selectedPolicy.policy_number,
                          selectedPolicy.source_id,
                          policyDetail.internal_policy_number
                        );
                      }
                      setAttachmentsExpanded(!attachmentsExpanded);
                    }}
                  >
                    <div className="attachments-header-left">
                      <span className="expand-icon">{attachmentsExpanded ? '▼' : '▶'}</span>
                      <AttachmentsIcon />
                      <h3>Documents</h3>
                      {attachmentsData?.success && attachmentsData.attachments?.length > 0 && (
                        <span className="attachments-count">({attachmentsData.attachments.length})</span>
                      )}
                    </div>
                    <div className="attachments-header-right">
                      {attachmentsLoading && <SearchSpinner />}
                    </div>
                  </div>

                  {attachmentsExpanded && (() => {
                    const allAtts = attachmentsData?.attachments || [];
                    const policyAtts = allAtts.filter(a => !a.display_claim_no);
                    const claimAttCount = allAtts.length - policyAtts.length;
                    return (
                    <div className="attachments-content">
                      {attachmentsLoading && (
                        <div className="attachments-loading">
                          <SearchSpinner />
                          <span>Loading documents...</span>
                        </div>
                      )}

                      {attachmentsData && !attachmentsLoading && !attachmentsData.success && (
                        <div className="attachments-error">
                          <span>Failed to load documents: {attachmentsData.error}</span>
                        </div>
                      )}

                      {attachmentsData && !attachmentsLoading && attachmentsData.success && policyAtts.length === 0 && (
                        <div className="attachments-empty">
                          <span>No policy-level documents{claimAttCount > 0 ? ` — ${claimAttCount} filed under claims (see Claims section)` : ' found for this policy'}</span>
                        </div>
                      )}

                      {attachmentsData && !attachmentsLoading && attachmentsData.success && policyAtts.length > 0 && (
                        <div className="attachments-list">
                          {claimAttCount > 0 && (
                            <div className="attachments-claim-hint">
                              {claimAttCount} claim document{claimAttCount === 1 ? '' : 's'} filed under the claims in the Claims section
                            </div>
                          )}
                          {policyAtts.map((att) => (
                            <div key={att.attachment_id} className="attachment-item">
                              <div className="attachment-info">
                                <span className="attachment-filename">{att.file_name}</span>
                                <span className="attachment-desc">
                                  {att.description || att.type_description}
                                  {att.is_confidential && <span className="attachment-confidential"> (Confidential)</span>}
                                </span>
                              </div>
                              <button
                                className="attachment-download-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadAttachment(att);
                                }}
                                disabled={downloadingAttachment !== null}
                                title={`Download ${att.file_name}`}
                              >
                                {downloadingAttachment === att.attachment_id ? (
                                  <SearchSpinner />
                                ) : (
                                  <DownloadIcon />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>

                <div className="detail-footer">
                  <span className="response-time">Loaded in {policyDetail.response_time_ms}ms</span>
                </div>
                </div>{/* end detail-scroll-content */}
              </>
            )}

            {policyDetail && !detailLoading && !policyDetail.success && (
              <div className="error-state">
                <div className="error-icon">⚠️</div>
                <h3>Failed to load</h3>
                <p>{policyDetail.error}</p>
              </div>
            )}

            {!selectedPolicy && !detailLoading && (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>Select a policy</h3>
                <p>Click a policy to view details</p>
              </div>
            )}
          </div>
        </div>
        </div>
      </main>
        } />
      </Routes>
      {itemModal && renderItemModal()}

      {/* Version browser modal */}
      {versionsOpen && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeVersions(); }}>
          <div className="admin-modal admin-modal-wide versions-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Policy versions{versionsMaxSeq ? ` (${versions.length} of ${versionsMaxSeq})` : ''}</h3>
              <button className="admin-modal-close" onClick={closeVersions}>&times;</button>
            </div>
            <div className="admin-modal-body versions-modal-body">
              <p className="versions-hint">Pick a version by its endorsement reason. <strong>Effective</strong> is when the change took effect; <strong>recorded</strong> is when it was captured. Each version is fetched from the policy admin system, so the full list can take a moment — you can select one as soon as it appears.</p>
              {versionsMaxSeq && versionsLoading && (
                <div className="versions-progress" title={`${versions.length} of ${versionsMaxSeq} loaded`}>
                  <div className="versions-progress-bar" style={{ width: `${Math.min(100, Math.round((versions.length / versionsMaxSeq) * 100))}%` }}></div>
                </div>
              )}
              {versionsError && <div className="history-error">{versionsError}</div>}
              {versions.length === 0 && versionsLoading && (
                <div className="versions-loading"><SearchSpinner /> Loading versions from the policy admin system…</div>
              )}
              <ul className="versions-list">
                {versions.map(v => (
                  <li
                    key={v.seq}
                    className={`version-row${historyBanner && String(historyBanner.seq_no) === String(v.seq) ? ' active' : ''}`}
                    onClick={() => pickVersion(v.seq)}
                  >
                    <div className="version-row-top">
                      <span className="version-seq">v{v.seq}</span>
                      <span className="version-eff">effective {formatDate(v.endorsement_date) || '—'}</span>
                      <span className="version-cap">recorded {formatDate(v.last_updated_date) || '—'}{v.updated_by ? ` · ${v.updated_by}` : ''}</span>
                    </div>
                    {v.updated_reason && <div className="version-reason">{v.updated_reason}</div>}
                  </li>
                ))}
              </ul>
              {versionsLoading && versions.length > 0 && (
                <div className="versions-loading more"><SearchSpinner /> Loading more…{versionsMaxSeq ? ` (${versions.length} of ${versionsMaxSeq})` : ''}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
}

// ============================================
// APP WITH ROUTER WRAPPER
// ============================================

function AppWithRouter() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}

export default AppWithRouter;